import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  FiSave,
  FiTrash2,
  FiCheck,
  FiX,
  FiRefreshCw,
  FiMic,
  FiMicOff,
  FiXCircle,
  FiGlobe, // Thêm icon cho WordPress
  FiFacebook, // Thêm icon cho Facebook
  FiAlertTriangle, // Thêm icon cảnh báo
  FiPackage,
  FiSearch,
} from "react-icons/fi"; // Thêm các icon mới
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getDatabase,
  ref,
  get,
  set,
  onValue,
} from "firebase/database";
import PointWarning from "./components/PointWarning";
import {
  POST_LENGTHS,
  POST_STYLES,
  DEFAULT_POST_LENGTH,
  DEFAULT_POST_STYLES,
  WORDPRESS_POST_LENGTHS,
  DEFAULT_WORDPRESS_POST_LENGTH,
} from "./constants/postSettings";
import PostLanguageSelector from "./components/PostLanguageSelector";
import ScheduleModeSelector from "./components/ScheduleModeSelector";
import FixedDateScheduleUI from "./components/FixedDateScheduleUI";
import axios from "axios";
import apiClient from "./api/config";
import ReviewQueuePanel from "./components/ReviewQueuePanel";
import GroundingToggle from "./components/GroundingToggle";
import SettingTooltip, {
  CONTACT_INFO_TOOLTIP_TEXT,
  FANPAGE_INFO_TOOLTIP_TEXT,
  WEBSITE_CONTACT_TOGGLE_TOOLTIP_TEXT,
} from "./components/SettingTooltip";
import {
  IMAGE_SOURCES,
  IMAGE_SOURCE_LABELS,
  DEFAULT_IMAGE_SOURCE,
  IMAGE_STYLE_OPTIONS,
  DEFAULT_IMAGE_STYLE,
  ASPECT_RATIO_OPTIONS,
  DEFAULT_ASPECT_RATIO,
  DEFAULT_IMAGE_SETTINGS,
  WP_IMAGE_COUNT_OPTIONS,
  DEFAULT_WP_IMAGE_COUNT,
  WP_AI_IMAGE_COUNT,
  FB_IMAGE_COUNT_OPTIONS,
  DEFAULT_FB_IMAGE_COUNT,
  UPLOAD_IMAGE_MAX_COUNT,
  UPLOAD_IMAGE_MAX_SIZE_BYTES,
} from "./services/aiImageService";
import UserImageUploader from "./components/UserImageUploader";
import {
  getSelectedWooProducts,
  requiresWooProductSelection,
} from "./utils/websiteProductSeo";
import { fetchAllWooProductsForSite } from "./utils/wooProductsApi";
import { normalizeWordPressSiteUrl } from "./utils/wordpressUrl";
import { buildFacebookAutoPostSettings } from "./utils/facebookAutoPostSettings";
import {
  PRODUCT_AUTO_POST_SOURCE_WOO,
  removePostConfigWithCronIndex,
  savePostConfigWithCronIndex,
} from "./utils/rtdbCronIndex";
import {
  getSocialAccountKey,
  loadConnectedSocialAccounts,
  toLegacyFacebookFanpagePayload,
  toSelectedAccountPayload,
} from "./utils/socialChannels";
import { buildSelectedWooProductsForSave } from "./utils/autoPostWooProducts";

const sanitizeWordpressCategoryIds = (categories = []) => {
  if (!Array.isArray(categories)) {
    return [];
  }

  const normalizedIds = categories
    .map((categoryId) => {
      const normalizedId = Number(categoryId);
      return Number.isInteger(normalizedId) && normalizedId > 0
        ? normalizedId
        : null;
    })
    .filter((categoryId) => categoryId !== null);

  return Array.from(new Set(normalizedIds));
};

const sanitizeWordpressCategories = (categories = []) => {
  if (!Array.isArray(categories)) {
    return [];
  }

  return categories
    .map((category) => {
      const normalizedId = Number(category?.id);
      if (!Number.isInteger(normalizedId) || normalizedId <= 0) {
        return null;
      }

      return {
        ...category,
        id: normalizedId,
      };
    })
    .filter(Boolean);
};

const WEBSITE_TOPIC_ASPECT_RATIO = "3:2";
const WEBSITE_TOPIC_ASPECT_RATIO_OPTIONS = ASPECT_RATIO_OPTIONS.map((opt) =>
  opt.value === "16:9"
    ? {
        ...opt,
        value: WEBSITE_TOPIC_ASPECT_RATIO,
        desc: "3:2 — Bài viết",
      }
    : opt,
);

function getCurrentUserIdOrThrow() {
  const userId = localStorage.getItem("userId");
  if (!userId) {
    throw new Error("Bạn cần đăng nhập để sử dụng tính năng này");
  }
  return userId;
}

async function suggestTopicsViaBackend({ source, info, language, topicCount }) {
  if (info && info.trim().length > 8000) {
    throw new Error("Thông tin gợi ý chủ đề không được vượt quá 8000 ký tự. Vui lòng rút ngắn nội dung!");
  }
  const { data } = await apiClient.post("/api/content/suggest-topics", {
    userId: getCurrentUserIdOrThrow(),
    source,
    info,
    language,
    topicCount,
  });

  if (!data?.success || !Array.isArray(data.topics)) {
    throw new Error(data?.message || "Phản hồi gợi ý chủ đề không hợp lệ");
  }

  return data.topics;
}

async function analyzeAutoPostRequestViaBackend({
  requestText,
  language,
  topicCountDefault,
}) {
  const { data } = await apiClient.post(
    "/api/content/analyze-auto-post-request",
    {
      userId: getCurrentUserIdOrThrow(),
      requestText,
      language,
      topicCountDefault,
      allowedPostLengths: [...POST_LENGTHS, ...WORDPRESS_POST_LENGTHS].map(
        (option) => option.value,
      ),
      allowedPostStyles: POST_STYLES.map((option) => option.value),
    },
  );

  if (!data?.success || !data.data) {
    throw new Error(data?.message || "Phản hồi phân tích yêu cầu không hợp lệ");
  }

  return data.data;
}

function aiRequestErrorMessage(error) {
  const apiMsg = error?.response?.data?.error?.message;
  const responseMsg = error?.response?.data?.message;
  if (responseMsg) return responseMsg;
  if (apiMsg) return apiMsg;
  if (error?.message) return error.message;
  return "Lỗi không xác định";
}

const resolveAspectRatioByContext = ({
  aspectRatio,
  platform,
  inputType,
  imageSource,
}) => {
  const isFacebookAiImage = platform === "facebook" && imageSource === IMAGE_SOURCES.AI;
  const isWebsiteTopicWithAiImage =
    platform === "wordpress" &&
    inputType === "topic" &&
    imageSource === IMAGE_SOURCES.AI;

  if (isFacebookAiImage) {
    // Facebook + AI image mặc định ưu tiên 1:1, chỉ fallback khi chưa có ratio hoặc ratio legacy.
    if (
      !aspectRatio ||
      aspectRatio === "16:9" ||
      aspectRatio === WEBSITE_TOPIC_ASPECT_RATIO
    ) {
      return DEFAULT_ASPECT_RATIO;
    }
    return aspectRatio;
  }

  // Zalo OA cover ưu tiên 16:9 (320x180 chuẩn)
  if (platform === "zalo") {
    return aspectRatio || "16:9";
  }

  if (!isWebsiteTopicWithAiImage) {
    return aspectRatio || DEFAULT_ASPECT_RATIO;
  }

  if (
    !aspectRatio ||
    aspectRatio === "16:9" ||
    aspectRatio === DEFAULT_ASPECT_RATIO
  ) {
    return WEBSITE_TOPIC_ASPECT_RATIO;
  }

  return aspectRatio;
};

const isFacebookProductPostMode = (platform, inputType) =>
  platform === "facebook" && inputType === "products";

const isSocialProductPostMode = (platform, inputType) =>
  (platform === "facebook" || platform === "zalo") && inputType === "products";

const resolveAllowProductLinksForContext = ({
  platform,
  inputType,
  allowProductLinks,
}) => {
  if (isSocialProductPostMode(platform, inputType)) {
    return allowProductLinks !== undefined ? allowProductLinks : true;
  }

  return allowProductLinks !== undefined ? allowProductLinks : true;
};

const AutoPostGenerator = () => {
  const [userMembership, setUserMembership] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [websitePostQuantity, setWebsitePostQuantity] = useState(0); // Thêm state mới để lưu số lượng bài viết website
  const location = useLocation();
  const config = location.state?.config || {};
  const isInitialWordpressProductMode =
    config.platform === "wordpress" && config.inputType === "products";
  const isInitialFacebookProductMode =
    config.platform === "facebook" && config.inputType === "products";
  const isInitialZaloProductMode =
    config.platform === "zalo" && config.inputType === "products";
  const initialProductCustomInfo = isInitialWordpressProductMode
    ? config.customInfo || config.prompt || ""
    : isInitialFacebookProductMode
      ? config.customInfo || config.prompt || ""
      : isInitialZaloProductMode
        ? config.customInfo || config.prompt || ""
        : config.customInfo || "";
  const initialAllowProductLinks = resolveAllowProductLinksForContext({
    platform: config.platform || "facebook",
    inputType: config.inputType === "products" ? "products" : "topic",
    allowProductLinks: config.allowProductLinks,
  });
  const userId = localStorage.getItem("userId");

  const [requirements, setRequirements] = useState(config.prompt || "");
  const [productsList, setProductsList] = useState(config.products || "");
  // Set default post length based on platform
  const [postLength, setPostLength] = useState(
    config.postLength ||
      (config.platform === "wordpress"
        ? DEFAULT_WORDPRESS_POST_LENGTH // "Trung bình" for WordPress
        : DEFAULT_POST_LENGTH), // "150 - 250 từ" for Facebook
  );
  const [postStyles, setPostStyles] = useState(
    config.postStyles ? config.postStyles.split(", ") : DEFAULT_POST_STYLES,
  );
  const [fanpages, setFanpages] = useState([]);
  const [selectedFanpages, setSelectedFanpages] = useState(
    config.fanpageID
      ? [{ id: config.fanpageID, name: config.fanpageName }]
      : [],
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Thêm thời gian mặc định 8h sáng nếu không có thời gian nào đã chọn
  const [selectedTimes, setSelectedTimes] = useState(
    config.times && config.times.length > 0 ? config.times : ["08:00"],
  );
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [wordpressInputType, setWordpressInputType] = useState(
    config.platform === "wordpress" && config.inputType === "products"
      ? "products"
      : "topic",
  );
  const [facebookInputType, setFacebookInputType] = useState(
    isInitialFacebookProductMode ? "products" : "topic",
  );
  const [zaloInputType, setZaloInputType] = useState(
    isInitialZaloProductMode ? "products" : "topic",
  );
  const inputType = "topic"; // Facebook luôn dùng topic mode
  const [topics, setTopics] = useState({}); // Changed from array to object/dictionary
  const [isGeneratingTopics, setIsGeneratingTopics] = useState(false); // New state for loading effect
  // const [products, setProducts] = useState([]); // Add this new state
  const [topicCount, setTopicCount] = useState(30); // Add this new state
  const [isTopicCountDropdownOpen, setIsTopicCountDropdownOpen] =
    useState(false); // Thêm state mới
  const topicCountDropdownRef = useRef(null); // Thêm ref mới
  const [isStyleDropdownOpen, setIsStyleDropdownOpen] = useState(false);
  const styleDropdownRef = useRef(null);
  const [userPoint, setUserPoint] = useState(0);
  const [autoPostEnabled, setAutoPostEnabled] = useState(
    config.id ? (config.autoPostEnabled ?? false) : true,
  ); // Default to true for new setups, use saved value for edits
  const [repeatWhenExhausted, setRepeatWhenExhausted] = useState(
    config.repeatWhenExhausted === true,
  );
  const [includeContactInfo, setIncludeContactInfo] = useState(
    config.includeContactInfo || false,
  );
  const [useFanpageInfo, setUseFanpageInfo] = useState(
    config.useFanpageInfo || false,
  );
  // Premium Grounding: opt-in toggle, default OFF
  const [useGoogleSearch, setUseGoogleSearch] = useState(
    config.useGoogleSearch === true || false,
  );
  const [allowNoImage, setAllowNoImage] = useState(
    config.allowNoImage || false,
  );
  const [imageSource, setImageSource] = useState(
    config.imageSource || DEFAULT_IMAGE_SOURCE,
  );
  const [imageStyle, setImageStyle] = useState(
    config.imageStyle || DEFAULT_IMAGE_STYLE,
  );
  const [aspectRatio, setAspectRatio] = useState(
    resolveAspectRatioByContext({
      aspectRatio: config.aspectRatio,
      platform: config.platform || "facebook",
      inputType: config.inputType === "products" ? "products" : "topic",
      imageSource: config.imageSource || DEFAULT_IMAGE_SOURCE,
    }),
  );
  const [customImagePrompt, setCustomImagePrompt] = useState(
    config.customImagePrompt || "",
  );
  const [platformType, setPlatformType] = useState("facebook"); // Default to facebook
  const [customInfo, setCustomInfo] = useState(initialProductCustomInfo); // Add customInfo state
  const [imageValidationEnabled, setImageValidationEnabled] = useState(
    config.imageValidationEnabled !== undefined
      ? config.imageValidationEnabled
      : false,
  ); // Default to false
  const [wpImageCount, setWpImageCount] = useState(
    config.wpImageCount || DEFAULT_WP_IMAGE_COUNT,
  );
  // Số ảnh cho bài viết Facebook khi nguồn là Upload (1-5, mặc định 1 để tương thích post cũ)
  const [fbImageCount, setFbImageCount] = useState(
    config.fbImageCount || DEFAULT_FB_IMAGE_COUNT,
  );

  // State cho danh sách ảnh user tự upload
  const [uploadedImages, setUploadedImages] = useState(
    Array.isArray(config.uploadedImages) ? config.uploadedImages : [],
  );

  // State cho chế độ đăng bài: "direct" (đăng ngay) hoặc "review" (duyệt trước)
  const [postMode, setPostMode] = useState(config.postMode || "direct");

  // Thêm state mới cho chức năng WordPress
  const [platform, setPlatform] = useState(config.platform || "facebook");
  const [wordpressConfig, setWordpressConfig] = useState({
    url: normalizeWordPressSiteUrl(config.wordpressUrl || ""),
    username: config.wordpressUsername || "",
    password: config.wordpressPassword || "",
    categories: sanitizeWordpressCategoryIds(config.wordpressCategories),
    tags: config.wordpressTags || "",
    status: config.wordpressStatus || "publish", // Default to publish status
  });
  const [isTestingWordpress, setIsTestingWordpress] = useState(false);

  // Thêm state cho danh sách categories
  const [availableCategories, setAvailableCategories] = useState([]);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef(null);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState("");
  // categoryMode: 'disabled' (tắt) hoặc 'manual' (chọn/nhập tay) hoặc 'ai' (AI tự chọn sau khi đăng)
  const [categoryMode, setCategoryMode] = useState("disabled");

  // Thêm state để lưu settings
  const [settings, setSettings] = useState({
    postLength:
      config.postLength ||
      (config.platform === "wordpress"
        ? DEFAULT_WORDPRESS_POST_LENGTH // "Trung bình" for WordPress
        : DEFAULT_POST_LENGTH), // "150 - 250 từ" for Facebook
    postStyles: config.postStyles
      ? config.postStyles.split(", ")
      : DEFAULT_POST_STYLES,
    includeContactInfo: config.includeContactInfo || false,
    useFanpageInfo: config.useFanpageInfo || false,
    useGoogleSearch: config.useGoogleSearch === true || false,
    autoPostEnabled: config.id ? (config.autoPostEnabled ?? false) : true,
    repeatWhenExhausted: config.repeatWhenExhausted === true,
    inputType: "topic", // Always use topic mode
    wordpressInputType:
      config.platform === "wordpress" && config.inputType === "products"
        ? "products"
        : "topic",
    facebookInputType: isInitialFacebookProductMode ? "products" : "topic",
    zaloInputType: isInitialZaloProductMode ? "products" : "topic",
    topicCount: 30,
    allowNoImage: config.allowNoImage || false,
    imageSource: config.imageSource || DEFAULT_IMAGE_SOURCE,
    imageStyle: config.imageStyle || DEFAULT_IMAGE_STYLE,
    aspectRatio: resolveAspectRatioByContext({
      aspectRatio: config.aspectRatio,
      platform: config.platform || "facebook",
      inputType:
        config.platform === "wordpress" && config.inputType === "products"
          ? "products"
          : "topic",
      imageSource: config.imageSource || DEFAULT_IMAGE_SOURCE,
    }),
    customImagePrompt: config.customImagePrompt || "",
    platformType: config.platformType || "facebook",
    platform: config.platform || "facebook",
    selectedLanguage: "vi",
    customInfo: initialProductCustomInfo, // Add customInfo to settings
    // Tính năng comment link sau khi đăng đã tắt mặc định và ẩn UI
    firstCommentEnabled: false,
    firstCommentMode: "manual",
    defaultFirstCommentText: "",
    firstCommentPrompt: "",
    imageValidationEnabled:
      config.imageValidationEnabled !== undefined
        ? config.imageValidationEnabled
        : false,
    wpImageCount: config.wpImageCount || DEFAULT_WP_IMAGE_COUNT,
    wordpressConfig: {
      url: normalizeWordPressSiteUrl(config.wordpressUrl || ""),
      username: config.wordpressUsername || "",
      password: config.wordpressPassword || "",
      categories: sanitizeWordpressCategoryIds(config.wordpressCategories),
      tags: config.wordpressTags || "",
      status: config.wordpressStatus || "publish", // Default to publish status
    },
    categoryMode: config.categoryMode || "disabled",
    postMode: config.postMode || "direct",
    allowProductLinks: initialAllowProductLinks,
  });
  const isWebsiteTopicMode =
    platform === "wordpress" && wordpressInputType === "topic";
  const aspectRatioOptions = isWebsiteTopicMode
    ? WEBSITE_TOPIC_ASPECT_RATIO_OPTIONS
    : ASPECT_RATIO_OPTIONS;

  // Thêm state cho selectedDays
  const [selectedDays, setSelectedDays] = useState(
    config.selectedDays || {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: true,
    },
  );

  // Thêm state cho selectedLanguage
  const [selectedLanguage, setSelectedLanguage] = useState("vi");


  // Thêm daysInVietnamese object
  const daysInVietnamese = {
    monday: "Thứ 2",
    tuesday: "Thứ 3",
    wednesday: "Thứ 4",
    thursday: "Thứ 5",
    friday: "Thứ 6",
    saturday: "Thứ 7",
    sunday: "Chủ nhật",
  };

  const [isAnalyzingRequest, setIsAnalyzingRequest] = useState(false); // Thêm state mới cho trạng thái phân tích

  // Thêm state mới để kiểm soát trạng thái đang lưu
  const [isSaving, setIsSaving] = useState(false);

  // Thêm các state mới cho chức năng ghi âm
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);

  // Thêm state mới để lưu transcript tạm thời
  const [tempTranscript, setTempTranscript] = useState("");

  // Thêm state mới cho danh sách WordPress sites
  const [wordpressSites, setWordpressSites] = useState([]);
  const [selectedWordpressSite, setSelectedWordpressSite] = useState(null);
  const [isWordpressSiteDropdownOpen, setIsWordpressSiteDropdownOpen] =
    useState(false);
  const wordpressSiteDropdownRef = useRef(null);

  // State cho việc nhập chủ đề thủ công
  const [manualTopic, setManualTopic] = useState("");
  const [wooProducts, setWooProducts] = useState([]);
  const [savedWooProducts, setSavedWooProducts] = useState(
    Array.isArray(config.products) ? config.products : [],
  );
  const [selectedWooProductIds, setSelectedWooProductIds] = useState(
    Array.isArray(config.selectedWooProductIds) &&
      config.selectedWooProductIds.length > 0
      ? config.selectedWooProductIds
      : Array.isArray(config.products)
        ? config.products
            .map((product) => product?.id)
            .filter((id) => id !== undefined && id !== null)
        : [],
  );
  const [allowWooProductLinks, setAllowWooProductLinks] = useState(
    initialAllowProductLinks,
  );
  const [isLoadingWooProducts, setIsLoadingWooProducts] = useState(false);
  const [wooProductsError, setWooProductsError] = useState("");
  const [hasLoadedWooProducts, setHasLoadedWooProducts] = useState(false);
  const [wooProductSearchQuery, setWooProductSearchQuery] = useState("");
  const [wooProductsMeta, setWooProductsMeta] = useState({
    count: 0,
    total: 0,
    totalPages: 0,
    fetchedAll: false,
    source: "",
  });

  // Thêm state cho schedule modes
  const [scheduleMode, setScheduleMode] = useState(
    config.scheduleMode || "weekly",
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fixedDateSchedules, setFixedDateSchedules] = useState(
    config.fixedDateSchedules || [],
  );
  const [runtimeProductPostingState, setRuntimeProductPostingState] = useState(
    config.productPostingState && typeof config.productPostingState === "object"
      ? config.productPostingState
      : {},
  );
  const [allProductsPostedAt, setAllProductsPostedAt] = useState(
    config.allProductsPostedAt || null,
  );

  const isWordpressProductMode =
    platform === "wordpress" && wordpressInputType === "products";
  const isFacebookWooProductMode =
    platform === "facebook" && facebookInputType === "products";
  const isZaloWooProductMode =
    platform === "zalo" && zaloInputType === "products";
  const isSocialWooProductMode =
    isFacebookWooProductMode || isZaloWooProductMode;
  const isWooProductMode =
    isWordpressProductMode || isSocialWooProductMode;
  const activeProductInputType =
    platform === "wordpress"
      ? wordpressInputType
      : platform === "zalo"
        ? zaloInputType
        : facebookInputType;
  const availablePostingAccounts = fanpages.filter(
    (account) => (account.platform || "facebook") === platform,
  );

  const getAutoPostCostPoints = () => {
    // Premium Grounding: +10 điểm khi user bật Google Search opt-in
    const groundingExtra = useGoogleSearch === true ? 10 : 0;

    if (platform === "wordpress") {
      if (isWordpressProductMode) {
        return 35 + groundingExtra;
      }

      if (imageSource === IMAGE_SOURCES.AI) {
        return 100 + groundingExtra;
      }

      return 35 + groundingExtra;
    }

    if (isFacebookWooProductMode || isZaloWooProductMode) {
      return 15; // WooCommerce flows giữ nguyên — đã có product data đủ chất lượng
    }

    if (imageSource === IMAGE_SOURCES.AI) {
      return 60 + groundingExtra;
    }

    if (imageSource === IMAGE_SOURCES.INTERNET && imageValidationEnabled) {
      return 12 + groundingExtra;
    }

    return 10 + groundingExtra;
  };

  const requiredAutoPostPoints = getAutoPostCostPoints();

  // 5 lượt viết bài website MIỄN PHÍ chỉ dành cho user ở gói Trải nghiệm (membership === 6).
  // User gói Cơ bản trở lên (1, 2, 3, 4) hoặc không có plan KHÔNG được dùng free.
  const hasFreeWebsiteQuota =
    userMembership === 6 && websitePostQuantity > 0;

  // Tất cả các gói đều trừ lượt hoặc điểm, không có miễn phí không giới hạn
  // Cao cấp/VIP (membership >= 3) = có quyền dùng tính năng, trừ lượt hoặc điểm
  // Trải nghiệm (6) = dùng free quota hoặc điểm; gói Cơ bản (1) trở lên KHÔNG được dùng free
  const canUseWordpressAutoPost =
    !!config.id ||
    hasFreeWebsiteQuota ||
    userPoint >= requiredAutoPostPoints;
  const isAutoPostSaveDisabled =
    isSaving ||
    (platform === "wordpress"
      ? hasFreeWebsiteQuota
        ? false // Còn lượt miễn phí (chỉ gói Trải nghiệm)
        : userPoint < requiredAutoPostPoints // Kiểm tra điểm
      : userPoint < requiredAutoPostPoints);

  const getAutoPostCostLabel = () => {
    if (platform === "wordpress") {
      if (hasFreeWebsiteQuota) {
        return `1 lượt đăng website/bài${
          userPoint > 0 ? ` (hết lượt: ${requiredAutoPostPoints} điểm)` : ""
        }`;
      }

      return `${requiredAutoPostPoints} điểm/bài`;
    }

    return `${requiredAutoPostPoints} điểm/bài`;
  };

  const selectedWooProductsSource =
    wooProducts.length > 0 ? wooProducts : savedWooProducts;
  const selectedWooProducts = getSelectedWooProducts(
    selectedWooProductsSource,
    selectedWooProductIds,
  );
  const totalFetchedWooProducts = wooProductsMeta.total || wooProducts.length;
  const filteredWooProducts = useMemo(() => {
    const q = wooProductSearchQuery.trim().toLowerCase();
    if (!q) return wooProducts;
    return wooProducts.filter((product) => {
      const name = (product.name || "").toLowerCase();
      const shortDesc = (product.shortDescription || "").toLowerCase();
      const link = (product.permalink || "").toLowerCase();
      const price = (product.priceText || "").toLowerCase();
      return (
        name.includes(q) ||
        shortDesc.includes(q) ||
        link.includes(q) ||
        price.includes(q)
      );
    });
  }, [wooProducts, wooProductSearchQuery]);
  const postedWooProductStateMap =
    runtimeProductPostingState &&
    typeof runtimeProductPostingState === "object"
      ? runtimeProductPostingState
      : {};
  const getWooProductPostingState = (product = {}) => {
    const id = product?.id;
    const permalink = String(product?.permalink || "")
      .trim()
      .toLowerCase();
    const name = String(product?.name || "")
      .trim()
      .toLowerCase();

    const candidateKeys = [];

    if (id !== undefined && id !== null && String(id).trim()) {
      const normalizedId = String(id).trim();
      candidateKeys.push(
        normalizedId,
        `product_id_${normalizedId}`,
        `product_id_${encodeURIComponent(normalizedId)}`,
      );
    }

    if (permalink) {
      candidateKeys.push(
        permalink,
        `product_url_${encodeURIComponent(permalink)}`,
      );
    }

    if (name) {
      candidateKeys.push(name, `product_name_${encodeURIComponent(name)}`);
    }

    for (const key of candidateKeys) {
      if (Object.prototype.hasOwnProperty.call(postedWooProductStateMap, key)) {
        return postedWooProductStateMap[key];
      }
    }

    return null;
  };
  const isWooProductPosted = (product = {}) => {
    const state = getWooProductPostingState(product);
    const normalizedStatus = String(
      state?.postStatus || state?.status || product?.postStatus || "",
    ).toLowerCase();

    return (
      normalizedStatus === "posted" ||
      state === true ||
      product?.isPosted === true
    );
  };
  const allWooProductsSelected =
    filteredWooProducts.length > 0 &&
    filteredWooProducts.every((product) =>
      selectedWooProductIds.some((id) => String(id) === String(product.id)),
    );
  const topicEntries = Object.values(topics || {});
  const hasTopicsConfigured = topicEntries.length > 0;
  const areAllTopicsPosted =
    hasTopicsConfigured &&
    topicEntries.every(
      (topic) => String(topic?.postStatus || "").toLowerCase() === "posted",
    );
  const selectedWooProductsToDisplay = getSelectedWooProducts(
    selectedWooProductsSource,
    selectedWooProductIds,
  );
  const hasSelectedWooProducts = selectedWooProductsToDisplay.length > 0;
  const areAllSelectedWooProductsPosted =
    hasSelectedWooProducts &&
    selectedWooProductsToDisplay.every((product) => isWooProductPosted(product));
  const showExhaustedLabel =
    activeProductInputType === "products"
      ? areAllSelectedWooProductsPosted || Boolean(allProductsPostedAt)
      : areAllTopicsPosted;

  // Lấy thông tin membership của user
  // OPTIMIZATION: Added cleanup for Firebase listener to prevent memory leak
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      const db = getDatabase();
      const membershipRef = ref(db, `/users/${userId}/membership`);

      const unsubscribe = onValue(membershipRef, (snapshot) => {
        if (snapshot.exists()) {
          setUserMembership(snapshot.val());
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, []);

  useEffect(() => {
    const loadFanpagesFromFirebase = async () => {
      if (localStorage.getItem("userId") == null) {
        navigate("/");
        return;
      }

      try {
        const db = getDatabase();
        const userId = localStorage.getItem("userId");
        const accountPlatformFilter =
          platform === "facebook" || platform === "zalo" ? platform : undefined;
        const fanpagesFromFirebase = await loadConnectedSocialAccounts(
          db,
          userId,
          accountPlatformFilter ? { platform: accountPlatformFilter } : {},
        );

        if (fanpagesFromFirebase.length > 0) {
          console.log("Accounts được tải từ Firebase:", fanpagesFromFirebase);
          setFanpages(fanpagesFromFirebase);

          if (fanpagesFromFirebase.length === 1) {
            setSelectedFanpages([fanpagesFromFirebase[0]]);
          } else {
            setSelectedFanpages([]);

            if (config.selectedAccounts && Array.isArray(config.selectedAccounts)) {
              const selectedKeys = new Set(config.selectedAccounts.map(getSocialAccountKey));
              const selectedAccounts = fanpagesFromFirebase.filter((account) =>
                selectedKeys.has(getSocialAccountKey(account))
              );
              if (selectedAccounts.length > 0) {
                setSelectedFanpages(selectedAccounts);
              }
            } else if (config.fanpageID) {
              const selectedFanpage = fanpagesFromFirebase.find(
                (fanpage) =>
                  (fanpage.platform || "facebook") === "facebook" &&
                  fanpage.id === config.fanpageID,
              );
              if (selectedFanpage) {
                setSelectedFanpages([selectedFanpage]);
              }
            }
          }
        } else {
          setFanpages([]);
          setSelectedFanpages([]);
          console.error("Không tìm thấy account Facebook/Zalo trong Firebase");
        }
      } catch (error) {
        console.error("Lỗi khi tải account từ Firebase:", error);
        toast.error("Có lỗi khi tải dữ liệu tài khoản. Vui lòng thử lại sau!", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    };

    loadFanpagesFromFirebase();
  }, [navigate, platform, config.fanpageID, config.selectedAccounts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Kiểm tra fanpage dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      // Kiểm tra topic count dropdown
      if (
        topicCountDropdownRef.current &&
        !topicCountDropdownRef.current.contains(event.target)
      ) {
        setIsTopicCountDropdownOpen(false);
      }
      // Kiểm tra style dropdown
      if (
        styleDropdownRef.current &&
        !styleDropdownRef.current.contains(event.target)
      ) {
        setIsStyleDropdownOpen(false);
      }
      // Kiểm tra category dropdown
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setIsCategoryDropdownOpen(false);
      }
      // Kiểm tra wordpress site dropdown
      if (
        wordpressSiteDropdownRef.current &&
        !wordpressSiteDropdownRef.current.contains(event.target)
      ) {
        setIsWordpressSiteDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Tự động gỡ bỏ flag 'isNew' sau 3 giây để tắt hiệu ứng highlight
  useEffect(() => {
    const hasNewTopics = Object.values(topics).some((topic) => topic.isNew);
    if (hasNewTopics) {
      const timer = setTimeout(() => {
        setTopics((prevTopics) => {
          const updatedTopics = {};
          let changed = false;
          Object.keys(prevTopics).forEach((key) => {
            if (prevTopics[key].isNew) {
              updatedTopics[key] = {
                ...prevTopics[key],
                isNew: false,
              };
              changed = true;
            } else {
              updatedTopics[key] = prevTopics[key];
            }
          });
          return changed ? updatedTopics : prevTopics;
        });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [topics]);

  useEffect(() => {
    if (config.id) {
      const db = getDatabase();
      const userId = localStorage.getItem("userId");
      const configRef = ref(db, `posts/${userId}/${config.id}`);

      get(configRef)
        .then((snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val();
            const loadedWordpressInputType =
              data.platform === "wordpress" && data.inputType === "products"
                ? "products"
                : "topic";
            const loadedFacebookInputType =
              data.platform === "facebook" && data.inputType === "products"
                ? "products"
                : "topic";
            const loadedZaloInputType =
              data.platform === "zalo" && data.inputType === "products"
                ? "products"
                : "topic";
            const loadedAllowProductLinks = resolveAllowProductLinksForContext({
              platform: data.platform || "facebook",
              inputType:
                data.platform === "wordpress"
                  ? loadedWordpressInputType
                  : data.platform === "zalo"
                    ? loadedZaloInputType
                    : loadedFacebookInputType,
              allowProductLinks: data.allowProductLinks,
            });
            const loadedSavedWooProducts = Array.isArray(data.products)
              ? data.products
              : [];
            const loadedSelectedWooProductIds =
              Array.isArray(data.selectedWooProductIds) &&
              data.selectedWooProductIds.length > 0
                ? data.selectedWooProductIds
                : loadedSavedWooProducts
                    .map((product) => product?.id)
                    .filter((id) => id !== undefined && id !== null);
            const isLoadedProductMode =
              loadedWordpressInputType === "products" ||
              loadedFacebookInputType === "products" ||
              loadedZaloInputType === "products";
            const loadedCustomInfo = isLoadedProductMode
              ? data.customInfo || data.prompt || ""
              : data.customInfo || "";
            const sanitizedWordpressCategories = sanitizeWordpressCategoryIds(
              data.wordpressCategories,
            );
            // Cập nhật tất cả các state từ dữ liệu config
            setRequirements(data.prompt || "");
            setProductsList(data.products || "");
            // Set default post length based on platform
            setPostLength(
              data.postLength ||
                (data.platform === "wordpress"
                  ? DEFAULT_WORDPRESS_POST_LENGTH // "Trung bình" for WordPress
                  : DEFAULT_POST_LENGTH),
            ); // "150 - 250 từ" for Facebook
            setPostStyles(
              data.postStyles
                ? data.postStyles.split(", ")
                : DEFAULT_POST_STYLES,
            );
            // Convert single time to array format if needed
            setSelectedTimes(
              data.times ? data.times : [data.time] || ["00:00"],
            );
            // Always use topic mode - inputType removed
            setAutoPostEnabled(data.autoPostEnabled || false);
            setRepeatWhenExhausted(data.repeatWhenExhausted === true);
            setIncludeContactInfo(data.includeContactInfo || false);
            setUseFanpageInfo(data.useFanpageInfo || false);
            setUseGoogleSearch(data.useGoogleSearch === true || false);
            setAllowNoImage(data.allowNoImage || false);
            setImageSource(data.imageSource || DEFAULT_IMAGE_SOURCE);
            setImageStyle(data.imageStyle || DEFAULT_IMAGE_STYLE);
            setAspectRatio(
              resolveAspectRatioByContext({
                aspectRatio: data.aspectRatio,
                platform: data.platform || "facebook",
                inputType:
                  data.platform === "wordpress"
                    ? loadedWordpressInputType
                    : data.platform === "zalo"
                      ? loadedZaloInputType
                      : loadedFacebookInputType,
                imageSource: data.imageSource || DEFAULT_IMAGE_SOURCE,
              }),
            );
            setCustomImagePrompt(data.customImagePrompt || "");
            setPostMode(data.postMode || "direct");
            setPlatform(data.platform || "facebook");
            setCustomInfo(loadedCustomInfo); // Load customInfo from config
            setWpImageCount(data.wpImageCount || DEFAULT_WP_IMAGE_COUNT);
            setFbImageCount(data.fbImageCount || DEFAULT_FB_IMAGE_COUNT);
            setWordpressInputType(loadedWordpressInputType);
            setFacebookInputType(loadedFacebookInputType);
            setZaloInputType(loadedZaloInputType);
            setAllowWooProductLinks(loadedAllowProductLinks);
            setSavedWooProducts(loadedSavedWooProducts);
            setSelectedWooProductIds(loadedSelectedWooProductIds);
            setRuntimeProductPostingState(
              data.productPostingState &&
                typeof data.productPostingState === "object"
                ? data.productPostingState
                : {},
            );
            setAllProductsPostedAt(data.allProductsPostedAt || null);

            // Load schedule mode and data
            const loadedScheduleMode = data.scheduleMode || "weekly";
            setScheduleMode(loadedScheduleMode);
            setFixedDateSchedules(data.fixedDateSchedules || []);

            // Load selectedDays if exists, merge with defaults to ensure all days have a value
            if (data.selectedDays) {
              setSelectedDays({
                monday: true,
                tuesday: true,
                wednesday: true,
                thursday: true,
                friday: true,
                saturday: true,
                sunday: true,
                ...data.selectedDays,
              });
            }

            // Cập nhật thông tin WordPress không bao gồm username/password
            setWordpressConfig({
              categories: sanitizedWordpressCategories,
              tags: data.wordpressTags || "",
              status: data.wordpressStatus || "publish",
              // Không lấy URL, username, password từ config nữa
              // url: data.wordpressUrl || "",
              // username: data.wordpressUsername || "",
              // password: data.wordpressPassword || "",
            });

            // Load categoryMode if exists
            if (data.categoryMode) {
              setCategoryMode(data.categoryMode);
            }

            // Cập nhật topics nếu có và config đang chạy theo chủ đề
            if (
              loadedWordpressInputType !== "products" &&
              data.topics &&
              Object.keys(data.topics).length > 0
            ) {
              setTopics(data.topics);
            } else {
              setTopics({});
            }

            // Load selectedLanguage từ data hoặc localStorage
            const languageFromConfig = data.selectedLanguage;
            const languageFromLocalStorage =
              localStorage.getItem("selectedLanguage");
            const finalLanguage =
              languageFromConfig || languageFromLocalStorage || "vi";
            setSelectedLanguage(finalLanguage);

            // Cập nhật settings
            setSettings({
              postLength:
                data.postLength ||
                (data.platform === "wordpress"
                  ? DEFAULT_WORDPRESS_POST_LENGTH // "Trung bình" for WordPress
                  : DEFAULT_POST_LENGTH), // "150 - 250 từ" for Facebook
              postStyles: data.postStyles
                ? data.postStyles.split(", ")
                : DEFAULT_POST_STYLES,
              includeContactInfo: data.includeContactInfo || false,
              useFanpageInfo: data.useFanpageInfo || false,
              useGoogleSearch: data.useGoogleSearch === true || false,
              autoPostEnabled: data.autoPostEnabled || false,
              repeatWhenExhausted: data.repeatWhenExhausted === true,
              postMode: data.postMode || "direct",
              inputType: "topic", // Always use topic mode
              wordpressInputType: loadedWordpressInputType,
              facebookInputType: loadedFacebookInputType,
              zaloInputType: loadedZaloInputType,
              topicCount: data.topicCount || 30,
              allowNoImage: data.allowNoImage || false,
              imageSource: data.imageSource || DEFAULT_IMAGE_SOURCE,
              imageStyle: data.imageStyle || DEFAULT_IMAGE_STYLE,
              aspectRatio: resolveAspectRatioByContext({
                aspectRatio: data.aspectRatio,
                platform: data.platform || "facebook",
                inputType:
                  data.platform === "wordpress"
                    ? loadedWordpressInputType
                    : data.platform === "zalo"
                      ? loadedZaloInputType
                      : loadedFacebookInputType,
                imageSource: data.imageSource || DEFAULT_IMAGE_SOURCE,
              }),
              customImagePrompt: data.customImagePrompt || "",
              platformType: data.platformType || "facebook",
              platform: data.platform || "facebook",
              selectedLanguage: finalLanguage,
              customInfo: loadedCustomInfo, // Add customInfo to settings
              wpImageCount: data.wpImageCount || DEFAULT_WP_IMAGE_COUNT,
              firstCommentEnabled: false,
              firstCommentMode: "manual",
              defaultFirstCommentText: "",
              firstCommentPrompt: "",
              wordpressConfig: {
                categories: sanitizedWordpressCategories,
                tags: data.wordpressTags || "",
                status: data.wordpressStatus || "publish",
                // Không lấy URL, username, password từ config nữa
                // url: data.wordpressUrl || "",
                // username: data.wordpressUsername || "",
                // password: data.wordpressPassword || "",
              },
              allowProductLinks: loadedAllowProductLinks,
            });

            // Update platformType if it exists in the data
            if (data.platformType) {
              setPlatformType(data.platformType);
            }

            // Load selected accounts từ config
            if (data.selectedAccounts && Array.isArray(data.selectedAccounts)) {
              setSelectedFanpages(data.selectedAccounts);
            } else if (data.selectedFanpages && Array.isArray(data.selectedFanpages)) {
              // Fanpages đã được lưu dưới dạng array
              setSelectedFanpages(data.selectedFanpages);
            } else if (data.fanpageID && data.fanpageName) {
              // Backward compatibility: convert single fanpage to array
              setSelectedFanpages([
                {
                  id: data.fanpageID,
                  name: data.fanpageName,
                  access_token: data.accessToken,
                },
              ]);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching config:", error);
          toast.error("Không thể tải dữ liệu cấu hình!", {
            position: "top-right",
            autoClose: 3000,
          });
        });
    }
  }, [config.id]);

  const fetchUserPoints = async () => {
    try {
      const db = getDatabase();
      const userId = localStorage.getItem("userId");
      // Cập nhật đường dẫn để lấy điểm
      const pointsRef = ref(db, `users/${userId}/points`);
      const snapshot = await get(pointsRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        if (data.points !== undefined && data.purchasedPoints !== undefined) {
          // Cấu trúc mới
          setUserPoint(data.points + data.purchasedPoints);
        } else if (typeof data.points === "number") {
          // Cấu trúc cũ
          setUserPoint(data.points);
        } else {
          console.error("Invalid points value:", data);
          setUserPoint(0);
        }
      } else {
        setUserPoint(0);
      }
    } catch (error) {
      console.error("Error fetching user points:", error);
      setUserPoint(0);
    }
  };

  // Hàm để lấy số lượng bài viết website còn lại
  const fetchWebsitePostQuantity = async () => {
    try {
      const db = getDatabase();
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const websitePostQuantityRef = ref(
        db,
        `users/${userId}/websitePostQuantity`,
      );
      const snapshot = await get(websitePostQuantityRef);

      if (snapshot.exists()) {
        setWebsitePostQuantity(snapshot.val());
      } else {
        setWebsitePostQuantity(0);
      }
    } catch (error) {
      console.error("Error fetching website post quantity:", error);
      setWebsitePostQuantity(0);
    }
  };

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      fetchUserPoints();
      fetchWebsitePostQuantity(); // Gọi hàm để lấy số lượng bài viết website
    }
  }, []);

  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const db = getDatabase();
        const settingsRef = ref(
          db,
          `users/${userId}/settings/autoPostGenerator`,
        );
        const snapshot = await get(settingsRef);

        if (snapshot.exists()) {
          const loadedSettings = snapshot.val();

          // Load selectedLanguage từ Firebase hoặc localStorage
          const languageFromFirebase = loadedSettings.selectedLanguage;
          const languageFromLocalStorage =
            localStorage.getItem("selectedLanguage");
          const finalLanguage =
            languageFromFirebase || languageFromLocalStorage || "vi";

          // Mỗi lần vào màn hình (thiết lập mới): luôn mặc định Facebook + Từ chủ đề,
          // không khôi phục nền tảng / chế độ Woo từ bản ghi settings cũ.
          const defaultPlatformOnLoad = "facebook";
          const defaultFacebookInputOnLoad = "topic";
          const defaultAllowProductLinksOnLoad =
            resolveAllowProductLinksForContext({
              platform: defaultPlatformOnLoad,
              inputType: defaultFacebookInputOnLoad,
              allowProductLinks: loadedSettings.allowProductLinks,
            });

          const updatedSettings = {
            ...loadedSettings,
            platform: defaultPlatformOnLoad,
            platformType: defaultPlatformOnLoad,
            facebookInputType: defaultFacebookInputOnLoad,
            zaloInputType: "topic",
            allowProductLinks: defaultAllowProductLinksOnLoad,
            selectedLanguage: finalLanguage,
            aspectRatio: resolveAspectRatioByContext({
              aspectRatio: loadedSettings.aspectRatio,
              platform: defaultPlatformOnLoad,
              inputType: defaultFacebookInputOnLoad,
              imageSource: loadedSettings.imageSource || DEFAULT_IMAGE_SOURCE,
            }),
            firstCommentEnabled: false,
            firstCommentMode: "manual",
            defaultFirstCommentText: "",
            firstCommentPrompt: "",
          };

          setSettings(updatedSettings);
          setSelectedLanguage(finalLanguage);

          // Cập nhật các state riêng lẻ
          // Độ dài bài: ưu tiên đã lưu, không thì mặc định theo Facebook (Siêu ngắn)
          setPostLength(
            loadedSettings.postLength || DEFAULT_POST_LENGTH,
          );
          setPostStyles(loadedSettings.postStyles || DEFAULT_POST_STYLES);
          setIncludeContactInfo(loadedSettings.includeContactInfo || false);
          setUseFanpageInfo(loadedSettings.useFanpageInfo || false);
          setUseGoogleSearch(loadedSettings.useGoogleSearch === true || false);
          setAutoPostEnabled(loadedSettings.autoPostEnabled || false);
          setRepeatWhenExhausted(
            loadedSettings.repeatWhenExhausted === true,
          );
          setPostMode(loadedSettings.postMode || "direct");
          setWordpressInputType(loadedSettings.wordpressInputType || "topic");
          setFacebookInputType(defaultFacebookInputOnLoad);
          setZaloInputType(loadedSettings.zaloInputType || "topic");
          setTopicCount(loadedSettings.topicCount || 30);
          setAllowNoImage(loadedSettings.allowNoImage || false);
          setImageSource(loadedSettings.imageSource || DEFAULT_IMAGE_SOURCE);
          setImageStyle(loadedSettings.imageStyle || DEFAULT_IMAGE_STYLE);
          setAspectRatio(
            resolveAspectRatioByContext({
              aspectRatio: loadedSettings.aspectRatio,
              platform: defaultPlatformOnLoad,
              inputType: defaultFacebookInputOnLoad,
              imageSource: loadedSettings.imageSource || DEFAULT_IMAGE_SOURCE,
            }),
          );
          setCustomImagePrompt(loadedSettings.customImagePrompt || "");
          setPlatformType(defaultPlatformOnLoad);
          setPlatform(defaultPlatformOnLoad);
          setAllowWooProductLinks(defaultAllowProductLinksOnLoad);
          setWordpressConfig(
            loadedSettings.wordpressConfig
              ? {
                  ...loadedSettings.wordpressConfig,
                  categories: sanitizeWordpressCategoryIds(
                    loadedSettings.wordpressConfig.categories,
                  ),
                }
              : {
                  url: "",
                  username: "",
                  password: "",
                  categories: [],
                  tags: "",
                  status: "publish", // Default to publish status
                },
          );
          setImageValidationEnabled(
            loadedSettings.imageValidationEnabled !== undefined
              ? loadedSettings.imageValidationEnabled
              : false,
          );
          setWpImageCount(loadedSettings.wpImageCount || DEFAULT_WP_IMAGE_COUNT);
        } else {
          // Nếu không có settings trong Firebase, load từ localStorage
          const languageFromLocalStorage =
            localStorage.getItem("selectedLanguage");
          const finalLanguage = languageFromLocalStorage || "vi";
          setSelectedLanguage(finalLanguage);
        }

        // Phần lấy danh sách WordPress sites đã được chuyển sang useEffect riêng
      } catch (error) {
        console.error("Error loading settings:", error);
      }
    };

    if (!config.id) {
      loadUserSettings();
    }
  }, []);

  // Thêm hàm saveUserSettings
  const saveUserSettings = async (newSettings = null) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const db = getDatabase();
      const settingsRef = ref(db, `users/${userId}/settings/autoPostGenerator`);

      // Nếu có newSettings thì dùng newSettings, không thì dùng state hiện tại
      const settingsToSave = newSettings || settings;

      await set(settingsRef, settingsToSave);
      // Cập nhật state settings
      setSettings(settingsToSave);
    } catch (error) {
      console.error("Error saving settings:", error);
    }
  };

  // Cập nhật các hàm xử lý sự kiện
  const handleIncludeContactInfoChange = () => {
    const newValue = !includeContactInfo;
    setIncludeContactInfo(newValue);
    const newSettings = {
      ...settings,
      includeContactInfo: newValue,
    };
    saveUserSettings(newSettings);
  };

  const handleUseFanpageInfoChange = () => {
    const newValue = !useFanpageInfo;
    setUseFanpageInfo(newValue);
    const newSettings = {
      ...settings,
      useFanpageInfo: newValue,
    };
    saveUserSettings(newSettings);
  };

  const handleUseGoogleSearchChange = (newValue) => {
    setUseGoogleSearch(newValue);
    const newSettings = {
      ...settings,
      useGoogleSearch: newValue,
    };
    saveUserSettings(newSettings);
  };

  const handleImageValidationChange = () => {
    const newValue = !imageValidationEnabled;
    setImageValidationEnabled(newValue);
    const newSettings = {
      ...settings,
      imageValidationEnabled: newValue,
    };
    saveUserSettings(newSettings);
  };

  const handlePostStyleChange = (style) => {
    const newStyles = postStyles.includes(style)
      ? postStyles.filter((s) => s !== style)
      : [...postStyles, style];
    setPostStyles(newStyles);
    const newSettings = {
      ...settings,
      postStyles: newStyles,
    };
    saveUserSettings(newSettings);
  };

  const resetWooProductsState = (clearSelection = true) => {
    setWooProducts([]);
    setWooProductsError("");
    setHasLoadedWooProducts(false);
    setWooProductSearchQuery("");
    setWooProductsMeta({
      count: 0,
      total: 0,
      totalPages: 0,
      fetchedAll: false,
      source: "",
    });

    if (clearSelection) {
      setSelectedWooProductIds([]);
      setSavedWooProducts([]);
    }
  };

  const fetchWooProductsForSite = async (site) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId || !site?.id) return;

      setIsLoadingWooProducts(true);
      setWooProductsError("");

      const { products, meta } = await fetchAllWooProductsForSite(
        site.id,
        userId,
      );

      if (Array.isArray(products)) {
        setWooProducts(products);
        setWooProductsMeta(meta);
        setHasLoadedWooProducts(true);

        if (products.length === 0) {
          toast.info("Website này chưa có sản phẩm WooCommerce public.", {
            position: "top-right",
            autoClose: 4000,
          });
        }
      } else {
        setWooProductsError("Không thể tải danh sách sản phẩm WooCommerce.");
        setWooProducts([]);
        setHasLoadedWooProducts(true);
      }
    } catch (error) {
      console.error("Error loading WooCommerce products:", error);
      const message =
        error.response?.data?.message ||
        "Đã xảy ra lỗi khi tải danh sách sản phẩm WooCommerce.";
      setWooProductsError(message);
      setWooProducts([]);
      setWooProductsMeta({
        count: 0,
        total: 0,
        totalPages: 0,
        fetchedAll: false,
        source: "",
      });
      setHasLoadedWooProducts(true);
    } finally {
      setIsLoadingWooProducts(false);
    }
  };

  const handleWordpressInputTypeChange = (nextType) => {
    const nextAspectRatio = resolveAspectRatioByContext({
      aspectRatio,
      platform,
      inputType: nextType,
      imageSource,
    });
    const nextAllowProductLinks = resolveAllowProductLinksForContext({
      platform,
      inputType: nextType,
      allowProductLinks: allowWooProductLinks,
    });
    setWordpressInputType(nextType);
    setAspectRatio(nextAspectRatio);
    setAllowWooProductLinks(nextAllowProductLinks);
    saveUserSettings({
      ...settings,
      wordpressInputType: nextType,
      aspectRatio: nextAspectRatio,
      allowProductLinks: nextAllowProductLinks,
    });
  };

  const handleFacebookInputTypeChange = (nextType) => {
    const nextAllowProductLinks = resolveAllowProductLinksForContext({
      platform,
      inputType: nextType,
      allowProductLinks: allowWooProductLinks,
    });
    setFacebookInputType(nextType);
    setAllowWooProductLinks(nextAllowProductLinks);
    saveUserSettings({
      ...settings,
      facebookInputType: nextType,
      allowProductLinks: nextAllowProductLinks,
    });
  };

  const handleZaloInputTypeChange = (nextType) => {
    const nextAllowProductLinks = resolveAllowProductLinksForContext({
      platform,
      inputType: nextType,
      allowProductLinks: allowWooProductLinks,
    });
    setZaloInputType(nextType);
    setAllowWooProductLinks(nextAllowProductLinks);
    setPostMode("direct");
    saveUserSettings({
      ...settings,
      zaloInputType: nextType,
      allowProductLinks: nextAllowProductLinks,
      postMode: "direct",
      firstCommentEnabled: false,
      firstCommentMode: "manual",
      defaultFirstCommentText: "",
      firstCommentPrompt: "",
    });
  };

  const handleAllowWooProductLinksChange = () => {
    if (isFacebookProductPostMode(platform, activeProductInputType)) {
      setAllowWooProductLinks(false);
      saveUserSettings({
        ...settings,
        allowProductLinks: false,
      });
      return;
    }

    const nextValue = !allowWooProductLinks;
    setAllowWooProductLinks(nextValue);
    saveUserSettings({
      ...settings,
      allowProductLinks: nextValue,
    });
  };

  const handleWooProductSelectionChange = (productId) => {
    setSelectedWooProductIds((prev) => {
      const exists = prev.some((id) => String(id) === String(productId));
      if (exists) {
        return prev.filter((id) => String(id) !== String(productId));
      }

      return [...prev, productId];
    });
  };

  const handleToggleSelectAllWooProducts = () => {
    setSelectedWooProductIds((prev) => {
      const currentProductIds = filteredWooProducts
        .map((product) => product?.id)
        .filter((id) => id !== undefined && id !== null);

      if (currentProductIds.length === 0) {
        return prev;
      }

      const areAllCurrentProductsSelected = currentProductIds.every(
        (productId) => prev.some((id) => String(id) === String(productId)),
      );

      if (areAllCurrentProductsSelected) {
        return prev.filter(
          (id) =>
            !currentProductIds.some(
              (productId) => String(productId) === String(id),
            ),
        );
      }

      const remainingIds = prev.filter(
        (id) =>
          !currentProductIds.some(
            (productId) => String(productId) === String(id),
          ),
      );

      return [...remainingIds, ...currentProductIds];
    });
  };

  const handleTopicCountChange = (newCount) => {
    setTopicCount(newCount);
    setIsTopicCountDropdownOpen(false);
    const newSettings = {
      ...settings,
      topicCount: newCount,
    };
    saveUserSettings(newSettings);
  };

  const handleAutoPostToggle = (e) => {
    const newValue = e.target.checked;
    setAutoPostEnabled(newValue);
    const newSettings = {
      ...settings,
      autoPostEnabled: newValue,
    };
    saveUserSettings(newSettings);
  };

  const handleRepeatWhenExhaustedToggle = (e) => {
    const newValue = e.target.checked;
    setRepeatWhenExhausted(newValue);
    const newSettings = {
      ...settings,
      repeatWhenExhausted: newValue,
    };
    saveUserSettings(newSettings);
  };

  // Xử lý thay đổi chế độ đăng bài (direct / review)
  const handlePostModeChange = (mode) => {
    setPostMode(mode);
    const newSettings = {
      ...settings,
      postMode: mode,
    };
    saveUserSettings(newSettings);
  };

  const handlePlatformTypeChange = (type) => {
    setPlatformType(type);
    const newSettings = {
      ...settings,
      platformType: type,
    };
    saveUserSettings(newSettings);
  };

  // Thêm hàm handleLanguageChange để xử lý thay đổi ngôn ngữ
  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);

    // Lưu vào localStorage
    localStorage.setItem("selectedLanguage", language);

    // Cập nhật settings và lưu vào Firebase
    const newSettings = {
      ...settings,
      selectedLanguage: language,
    };
    saveUserSettings(newSettings);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleFanpageSelect = (fanpage) => {
    // Topic mode - cho phép chọn nhiều fanpage
    // Kiểm tra xem fanpage đã được chọn chưa
    const targetKey = getSocialAccountKey(fanpage);
    const isAlreadySelected = selectedFanpages.some(
      (fp) => getSocialAccountKey(fp) === targetKey,
    );

    if (isAlreadySelected) {
      // Nếu đã chọn rồi thì bỏ chọn
      const updatedFanpages = selectedFanpages.filter(
        (fp) => getSocialAccountKey(fp) !== targetKey,
      );
      setSelectedFanpages(updatedFanpages);
      console.log("Đã bỏ chọn account:", fanpage.name);
    } else {
      // Nếu chưa chọn thì thêm vào danh sách
      setSelectedFanpages([...selectedFanpages, fanpage]);
      console.log("Đã thêm account:", fanpage.name);
    }
  };

  const generateFacebookTopics = (isAppend = false) => {
    if (!requirements.trim()) {
      toast.error("Vui lòng nhập yêu cầu để tạo chủ đề!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsGeneratingTopics(true);

    suggestTopicsViaBackend({
      source: "auto-post-facebook",
      info: requirements,
      language: selectedLanguage,
      topicCount,
    })
      .then((filteredData) => {
        const timestamp = Date.now();
        const topicsDict = filteredData.reduce((acc, topic, index) => {
          const id = `topic-${timestamp}-${index + 1}`;
          acc[id] = {
            id,
            title: topic,
            status: "pending",
            postStatus: "not_started",
            createdAt: timestamp + index,
            isNew: true,
          };
          return acc;
        }, {});
        setTopics((prev) => {
          const baseTopics = isAppend ? { ...prev } : {};
          return {
            ...baseTopics,
            ...topicsDict,
          };
        });
        toast.success(
          isAppend
            ? "Đã tạo thêm chủ đề Facebook mới!"
            : "Danh sách chủ đề Facebook đã được tạo!",
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
      })
      .catch((error) => {
        console.error("Failed to generate Facebook topics:", error);
        toast.error(
          `Tạo danh sách chủ đề Facebook thất bại: ${aiRequestErrorMessage(error)}`,
          {
            position: "top-right",
            autoClose: 5000,
          },
        );
      })
      .finally(() => {
        setIsGeneratingTopics(false);
      });
  };

  const generateWordpressTopics = (isAppend = false) => {
    if (!requirements.trim()) {
      toast.error("Vui lòng nhập yêu cầu để tạo chủ đề!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsGeneratingTopics(true);

    suggestTopicsViaBackend({
      source: "auto-post-wordpress",
      info: requirements,
      language: selectedLanguage,
      topicCount,
    })
      .then((filteredData) => {
        const timestamp = Date.now();
        const topicsDict = filteredData.reduce((acc, topic, index) => {
          const id = `topic-${timestamp}-${index + 1}`;
          acc[id] = {
            id,
            title: topic,
            status: "pending",
            postStatus: "not_started",
            createdAt: timestamp + index,
            isNew: true,
          };
          return acc;
        }, {});
        setTopics((prev) => {
          const baseTopics = isAppend ? { ...prev } : {};
          return {
            ...baseTopics,
            ...topicsDict,
          };
        });
        toast.success(
          isAppend
            ? "Đã tạo thêm chủ đề WordPress mới!"
            : "Danh sách chủ đề WordPress đã được tạo!",
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
      })
      .catch((error) => {
        console.error("Failed to generate WordPress topics:", error);
        toast.error(
          `Tạo danh sách chủ đề WordPress thất bại: ${aiRequestErrorMessage(error)}`,
          {
            position: "top-right",
            autoClose: 5000,
          },
        );
      })
      .finally(() => {
        setIsGeneratingTopics(false);
      });
  };

  const deleteTopic = (topicId) => {
    setTopics((prevTopics) => {
      const newTopics = { ...prevTopics };
      delete newTopics[topicId];
      return newTopics;
    });
  };

  const handleAddManualTopic = () => {
    if (!manualTopic.trim()) {
      toast.error("Vui lòng nhập nội dung chủ đề!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const newTopicId = `manual_${Date.now()}`;
    const newTopic = {
      id: newTopicId,
      title: manualTopic.trim(),
      status: "pending",
      postStatus: "not_started",
      createdAt: Date.now(),
    };

    setTopics((prev) => ({
      ...prev,
      [newTopicId]: newTopic,
    }));

    setManualTopic("");
    toast.success("Đã thêm chủ đề mới!", {
      position: "top-right",
      autoClose: 2000,
    });
  };

  const resetTopicsStatus = () => {
    setTopics((prevTopics) => {
      const updatedTopics = {};
      Object.keys(prevTopics).forEach((key) => {
        const {
          failReason,
          failedAt,
          failureReasonCode,
          failureMessage,
          postedAt,
          wordpressPostId,
          wordpressPostUrl,
          uniqueKey,
          isDuplicatePost,
          duplicateNote,
          hasPostError,
          lastUpdateTime,
          ...topicWithoutProgressMeta
        } = prevTopics[key] || {};
        updatedTopics[key] = {
          ...topicWithoutProgressMeta,
          status: "pending",
          postStatus: "not_started",
        };
      });
      return updatedTopics;
    });

    toast.success("Đã reset trạng thái các chủ đề!", {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const clearAllTopics = () => {
    setTopics({});
    toast.success("Đã xóa toàn bộ danh sách chủ đề!", {
      position: "top-right",
      autoClose: 2000,
    });
  };

  // Thêm hàm xử lý thay đổi ngày
  const handleDayChange = (day) => {
    setSelectedDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  // Thêm các handler functions cho fixed-date schedules
  const handleAddFixedDateSchedule = (schedule) => {
    const newSchedule = {
      ...schedule,
      id: `schedule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      completed: false,
    };
    setFixedDateSchedules([...fixedDateSchedules, newSchedule]);
    toast.success("Đã thêm lịch đăng mới!");
  };

  const handleEditFixedDateSchedule = (index, updates) => {
    const updated = [...fixedDateSchedules];
    updated[index] = { ...updated[index], ...updates };
    setFixedDateSchedules(updated);
    toast.success("Đã cập nhật lịch đăng!");
  };

  const handleDeleteFixedDateSchedule = (index) => {
    setFixedDateSchedules(fixedDateSchedules.filter((_, i) => i !== index));
    toast.success("Đã xóa lịch đăng!");
  };

  const handleToggleScheduleCompleted = (index) => {
    const updated = [...fixedDateSchedules];
    updated[index] = {
      ...updated[index],
      completed: !updated[index].completed,
      completedAt: !updated[index].completed ? new Date().toISOString() : null,
    };
    setFixedDateSchedules(updated);
  };

  const saveAutoPostSettings = async () => {
    if (isSaving) return; // Ngăn chặn việc gọi nhiều lần khi đang lưu

    const selectedWooProductsToSave =
      buildSelectedWooProductsForSave(selectedWooProducts);
    const previousSelectedWooProductIds =
      Array.isArray(config.selectedWooProductIds) &&
      config.selectedWooProductIds.length > 0
        ? config.selectedWooProductIds.map((id) => String(id))
        : Array.isArray(config.products)
          ? config.products
              .map((product) => product?.id)
              .filter((id) => id !== undefined && id !== null)
              .map((id) => String(id))
          : [];
    const currentSelectedWooProductIds = selectedWooProductsToSave
      .map((product) => product?.id)
      .filter((id) => id !== undefined && id !== null)
      .map((id) => String(id));
    const shouldResetProductCursor =
      activeProductInputType === "products" &&
      (config.inputType !== "products" ||
        previousSelectedWooProductIds.join("|") !==
          currentSelectedWooProductIds.join("|"));

    if (
      activeProductInputType !== "products" &&
      Object.keys(topics).length === 0
    ) {
      toast.error("Vui lòng tạo hoặc thêm ít nhất 1 chủ đề trước khi lưu!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (platform === "facebook" || platform === "zalo") {
      if (userPoint < requiredAutoPostPoints) {
        toast.error(
          `Bạn cần ít nhất ${requiredAutoPostPoints} điểm để lưu thiết lập tự động này!`,
          {
            position: "top-right",
            autoClose: 3000,
          },
        );
        return;
      }

      if (selectedFanpages.length === 0) {
        toast.error("Vui lòng chọn ít nhất một tài khoản đăng bài!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // Kiểm tra tất cả fanpages đã chọn có access token trong state
      // Lưu ý: Backend TỰ lấy access_token từ Firebase connectedFanpages khi đăng bài
      // nên check này chỉ là cảnh báo, KHÔNG block lưu thiết lập
      const invalidFanpages = selectedFanpages.filter(
        (fp) => (fp.platform || "facebook") === "facebook" && !fp.access_token,
      );
      if (invalidFanpages.length > 0) {
        console.warn(
          `[Lưu thiết lập] Một số fanpage thiếu access_token trong state (sẽ lấy từ Firebase khi đăng): ${invalidFanpages
            .map((fp) => fp.name)
            .join(", ")}`,
        );
      }

      if ((isFacebookWooProductMode || isZaloWooProductMode) && !selectedWordpressSite) {
        toast.error("Vui lòng chọn website chứa sản phẩm WooCommerce!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      if (
        (isFacebookWooProductMode || isZaloWooProductMode) &&
        (requiresWooProductSelection(true, selectedWooProductIds) ||
          selectedWooProductsToSave.length === 0)
      ) {
        toast.error(
          "Vui lòng chọn ít nhất 1 sản phẩm WooCommerce để tạo bài từ sản phẩm.",
          {
            position: "top-right",
            autoClose: 4000,
          },
        );
        return;
      }
    } else if (platform === "wordpress") {
      // Kiểm tra điều kiện sử dụng chức năng đăng bài lên website
      // Nếu đang chỉnh sửa thiết lập đã lưu (có config.id), cho phép lưu bất kể số lượt đăng bài
      if (!config.id && userMembership < 3 && !hasFreeWebsiteQuota) {
        toast.error(
          "Bạn cần có gói Cao cấp hoặc có lượt đăng bài website để sử dụng tính năng này",
          {
            position: "top-right",
            autoClose: 3000,
          },
        );
        return;
      }

      if (!selectedWordpressSite) {
        toast.error("Vui lòng chọn một website WordPress!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      if (
        isWordpressProductMode &&
        (requiresWooProductSelection(true, selectedWooProductIds) ||
          selectedWooProductsToSave.length === 0)
      ) {
        toast.error(
          "Vui lòng chọn ít nhất 1 sản phẩm WooCommerce để tạo bài tự động từ sản phẩm.",
          {
            position: "top-right",
            autoClose: 4000,
          },
        );
        return;
      }
    }

    // Validation cho schedule modes
    if (scheduleMode === "weekly") {
      const selectedDayCount =
        Object.values(selectedDays).filter(Boolean).length;
      if (selectedDayCount === 0) {
        toast.error("Vui lòng chọn ít nhất một ngày đăng.", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      if (!selectedTimes.length) {
        toast.error("Vui lòng thêm ít nhất một khung giờ đăng.", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
    } else if (scheduleMode === "fixed-dates") {
      if (fixedDateSchedules.length === 0) {
        toast.error("Vui lòng thêm ít nhất một lịch đăng!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // Validate each schedule
      for (const schedule of fixedDateSchedules) {
        if (!schedule.date) {
          toast.error("Mỗi lịch đăng phải có ngày.", {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }

        if (!schedule.times || schedule.times.length === 0) {
          toast.error("Mỗi lịch đăng phải có ít nhất một giờ.", {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }
      }
    }

    setIsSaving(true); // Bắt đầu quá trình lưu

    // ===== DEFERRED UPLOAD: Upload ảnh local lên Cloudinary trước khi lưu =====
    let finalUploadedImages = uploadedImages;
    if (imageSource === IMAGE_SOURCES.UPLOAD) {
      const pendingImages = uploadedImages.filter(img => img._isLocal);
      if (pendingImages.length > 0) {
        try {
          toast.info(`Đang upload ${pendingImages.length} ảnh...`, {
            position: "top-right",
            autoClose: false,
            toastId: "uploading-images",
          });
          finalUploadedImages = await uploadPendingImages(uploadedImages);
          setUploadedImages(finalUploadedImages);
          toast.dismiss("uploading-images");
        } catch (uploadError) {
          toast.dismiss("uploading-images");
          console.error("[saveAutoPostSettings] Upload ảnh thất bại:", uploadError);
          toast.error(`Upload ảnh thất bại: ${uploadError.message}`, {
            position: "top-right",
            autoClose: 5000,
          });
          setIsSaving(false);
          return;
        }
      }
    }

    const db = getDatabase();
    const userId = localStorage.getItem("userId");
    const effectiveInputType =
      platform === "wordpress"
        ? wordpressInputType
        : platform === "zalo"
          ? zaloInputType
          : facebookInputType;
    const effectiveAllowProductLinks = resolveAllowProductLinksForContext({
      platform,
      inputType: effectiveInputType,
      allowProductLinks: allowWooProductLinks,
    });
    const effectiveUseFanpageInfo =
      (platform === "facebook" || platform === "zalo") &&
      selectedFanpages.length >= 2
        ? false
        : useFanpageInfo;
    const promptSettings =
      platform === "facebook" || platform === "zalo"
        ? buildFacebookAutoPostSettings({
            selectedFanpages,
            postLength,
            postStyles,
            customInfo,
            includeContactInfo,
            useFanpageInfo: effectiveUseFanpageInfo,
            selectedLanguage,
            postMode: platform === "zalo" ? "direct" : postMode,
            useGrounding: useGoogleSearch === true,
          })
        : {
            postLength,
            postStyles: postStyles.join(", "),
            includeContactInfo,
            useFanpageInfo: effectiveUseFanpageInfo,
            selectedLanguage,
            customInfo,
            postMode,
            useGrounding: useGoogleSearch === true,
          };

    const settingsToSave = {
      platform, // Lưu nền tảng được chọn
      productSource:
        activeProductInputType === "products"
          ? PRODUCT_AUTO_POST_SOURCE_WOO
          : null,
      inputType: effectiveInputType,
      prompt: activeProductInputType === "products" ? "" : requirements,
      products:
        activeProductInputType === "products" ? selectedWooProductsToSave : "",
      // Save both formats for backwards compatibility
      time: null, // Keep the first time for old format
      times: selectedTimes, // Save full array for new format
      autoPostEnabled: autoPostEnabled,
      repeatWhenExhausted: repeatWhenExhausted,
      userID: userId,
      postLength: promptSettings.postLength,
      postStyles: promptSettings.postStyles,
      topics:
        activeProductInputType === "products"
          ? {}
          : inputType === "topic"
            ? topics
            : {},
      includeContactInfo: promptSettings.includeContactInfo,
      useFanpageInfo: promptSettings.useFanpageInfo,
      useGrounding: useGoogleSearch === true, // Premium Grounding opt-in
      topicCount: topicCount,
      selectedDays: selectedDays,
      allowNoImage: allowNoImage,
      imageSource: imageSource,
      imageStyle: imageStyle,
      aspectRatio: aspectRatio,
      customImagePrompt: customImagePrompt,
      platformType: platformType,
      selectedLanguage: promptSettings.selectedLanguage, // Thêm selectedLanguage vào settings
      customInfo: promptSettings.customInfo, // Add customInfo to settings
      ...(promptSettings.promptProfileVersion && {
        promptProfileVersion: promptSettings.promptProfileVersion,
      }),
      firstCommentEnabled: false,
      firstCommentMode: "manual",
      defaultFirstCommentText: "",
      firstCommentPrompt: "",
      imageValidationEnabled: imageValidationEnabled, // Lưu trạng thái kiểm duyệt ảnh
      wpImageCount:
        platform === "wordpress" ? wpImageCount : config.wpImageCount || DEFAULT_WP_IMAGE_COUNT, // Số lượng ảnh cho bài viết WordPress
      fbImageCount:
        platform === "facebook" ? fbImageCount : config.fbImageCount || DEFAULT_FB_IMAGE_COUNT, // Số lượng ảnh cho bài viết Facebook (Upload)
      postMode: platform === "zalo" ? "direct" : promptSettings.postMode, // Lưu chế độ đăng bài (direct / review)
      allowProductLinks: effectiveAllowProductLinks,
      selectedWooProductIds:
        activeProductInputType === "products"
          ? currentSelectedWooProductIds
          : [],
      // Schedule mode fields
      scheduleMode: scheduleMode || "weekly",
      ...(scheduleMode === "weekly" && {
        selectedDays,
        times: selectedTimes,
      }),
      ...(scheduleMode === "fixed-dates" && {
        fixedDateSchedules,
      }),
      // Upload images - lưu khi nguồn ảnh là upload (đã upload Cloudinary ở trên)
      ...(imageSource === IMAGE_SOURCES.UPLOAD && {
        uploadedImages: finalUploadedImages.map((img, idx) => ({
          url: img.url,
          filename: img.filename || img.name || "",
          order: idx + 1,
        })),
        uploadImageCursor: config.uploadImageCursor || 0,
      }),
      // Xóa uploadedImages khi đổi sang nguồn ảnh khác
      ...(imageSource !== IMAGE_SOURCES.UPLOAD && {
        uploadedImages: null,
        uploadImageCursor: null,
      }),

    };

    if (
      selectedWordpressSite &&
      (platform === "wordpress" || isFacebookWooProductMode || isZaloWooProductMode)
    ) {
      settingsToSave.wordpressSiteId = selectedWordpressSite.id;
      settingsToSave.wordpressSiteName = selectedWordpressSite.siteName;
    }

    // Thêm thông tin dựa vào nền tảng
    if (platform === "facebook" || platform === "zalo") {
      settingsToSave.selectedAccounts = selectedFanpages.map(toSelectedAccountPayload);

      if (platform === "facebook") {
        // Lưu thông tin tất cả fanpages đã chọn
        settingsToSave.selectedFanpages = selectedFanpages
          .filter((account) => (account.platform || "facebook") === "facebook")
          .map(toLegacyFacebookFanpagePayload);

        // Backward compatibility: lưu fanpage đầu tiên với format cũ
        const firstFanpage = settingsToSave.selectedFanpages[0] || {};
        settingsToSave.accessToken = firstFanpage.access_token || "";
        settingsToSave.fanpageID = firstFanpage.id || "";
        settingsToSave.fanpageName = firstFanpage.name || "";
        settingsToSave.imageURLs = "";
      } else {
        settingsToSave.selectedFanpages = [];
        settingsToSave.accessToken = "";
        settingsToSave.fanpageID = "";
        settingsToSave.fanpageName = "";
        settingsToSave.imageURLs = "";
      }
    } else if (platform === "wordpress") {
      // Lưu các cấu hình khác của WordPress ngoại trừ thông tin nhạy cảm
      settingsToSave.wordpressCategories = sanitizeWordpressCategoryIds(
        wordpressConfig.categories,
      );
      settingsToSave.wordpressTags = wordpressConfig.tags;
      settingsToSave.wordpressStatus = wordpressConfig.status;
      settingsToSave.categoryMode = categoryMode;

      // Không lưu URL, username và password
      // settingsToSave.wordpressUrl = wordpressConfig.url;
      // settingsToSave.wordpressUsername = wordpressConfig.username;
      // settingsToSave.wordpressPassword = wordpressConfig.password;
    }

    settingsToSave.productCursor =
      activeProductInputType === "products"
        ? shouldResetProductCursor
          ? 0
          : Number.isFinite(Number(config.productCursor))
            ? Number(config.productCursor)
            : 0
        : 0;
    settingsToSave.productPostingState =
      activeProductInputType === "products"
        ? shouldResetProductCursor
          ? {}
          : config.productPostingState &&
              typeof config.productPostingState === "object"
            ? config.productPostingState
            : {}
        : {};

    if (shouldResetProductCursor || activeProductInputType !== "products") {
      settingsToSave.lastAutoPostedProductId = null;
      settingsToSave.lastAutoPostedProductName = "";
      settingsToSave.lastProductPostedAt = null;
      settingsToSave.lastProductError = null;
      settingsToSave.lastFailedProductAt = null;
      settingsToSave.allProductsPostedAt = null;
    }

    setRuntimeProductPostingState(settingsToSave.productPostingState || {});
    setAllProductsPostedAt(settingsToSave.allProductsPostedAt || null);

    const saveOperation = savePostConfigWithCronIndex(
      db,
      userId,
      config.id,
      settingsToSave
    );

    saveOperation
      .then(() => {
        // Số lượt đăng bài sẽ được trừ ở phía BE
        // Chỉ hiển thị thông báo nếu đang tạo thiết lập mới (không phải edit) và platform là wordpress
        if (
          !config.id &&
          platform === "wordpress" &&
          userMembership < 3 &&
          hasFreeWebsiteQuota
        ) {
          toast.info(
            `Thiết lập đã được lưu. Số lượt đăng bài website sẽ được trừ khi bài viết được đăng lên website.`,
            {
              position: "top-right",
              autoClose: 5000,
            },
          );
        }

        toast.success("Thiết lập đăng bài tự động đã được lưu!", {
          position: "top-right",
          autoClose: 3000,
        });

        // Clear dữ liệu và reset trang nếu là tạo mới (không có config.id)
        if (!config.id) {
          setTimeout(() => {
            resetConfig();
          }, 1000); // Chờ 1 giây sau khi lưu thành công
        }
      })
      .catch((error) => {
        console.error("Failed to save settings:", error);
        toast.error("Lưu thiết lập thất bại!", {
          position: "top-right",
          autoClose: 3000,
        });
      })
      .finally(() => {
        setIsSaving(false); // Kết thúc quá trình lưu
      });
  };

  const resetConfig = () => {
    setRequirements("");
    setProductsList("");
    // Set default post length based on platform
    setPostLength(
      platform === "wordpress"
        ? DEFAULT_WORDPRESS_POST_LENGTH // "Trung bình" for WordPress
        : DEFAULT_POST_LENGTH,
    ); // "150 - 250 từ" for Facebook
    setPostStyles(DEFAULT_POST_STYLES);
    setSelectedFanpages([]);
    setSelectedTimes([]);
    setTopics({});
    setWordpressInputType("topic");
    setFacebookInputType("topic");
    setSelectedWooProductIds([]);
    setSavedWooProducts([]);
    setWooProducts([]);
    setWooProductsError("");
    setHasLoadedWooProducts(false);
    setWooProductSearchQuery("");
    setAllowWooProductLinks(true);
    setPlatform("facebook"); // Reset nền tảng về mặc định
    setCustomInfo(""); // Reset customInfo
    setSelectedWordpressSite(null);
    setWordpressConfig({
      url: "",
      username: "",
      password: "",
      categories: [],
      tags: "",
      status: "publish", // Default to publish status
    });
    setSelectedDays({
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: true,
    });
    navigate("/auto-post", { state: {} });
  };

  const deleteConfig = () => {
    if (!config.id) {
      toast.error("Không có thiết lập để xóa!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    const db = getDatabase();
    const userId = localStorage.getItem("userId");

    removePostConfigWithCronIndex(db, userId, config.id)
      .then(() => {
        toast.success("Đã xóa thiết lập thành công!", {
          position: "top-right",
          autoClose: 3000,
        });
        resetConfig();
      })
      .catch((error) => {
        console.error("Failed to delete config:", error);
        toast.error("Xóa thiết lập thất bại!", {
          position: "top-right",
          autoClose: 3000,
        });
      });
  };

  // Thêm hàm phân tích yêu cầu tự động
  const analyzeRequest = () => {
    // Dừng ghi âm nếu đang ghi
    if (isRecording && recognition) {
      recognition.stop();
      setIsRecording(false);
      setTempTranscript("");
    }

    // Lấy text mới nhất từ requirements và tempTranscript
    const currentText = `${requirements}${
      tempTranscript ? ` ${tempTranscript}` : ""
    }`.trim();

    if (!currentText) {
      toast.error("Vui lòng nhập yêu cầu để phân tích!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (userPoint <= 0) {
      toast.error("Bạn không đủ điểm để sử dụng tính năng này!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsAnalyzingRequest(true);

    analyzeAutoPostRequestViaBackend({
      requestText: currentText,
      language: selectedLanguage,
      topicCountDefault: topicCount,
    })
      .then((parsedData) => {
        try {
          // Cập nhật các state từ kết quả phân tích
          if (parsedData.topics && parsedData.topics.length > 0) {
            // Nếu có topicCount, sử dụng nó để giới hạn số lượng chủ đề
            const topicLimit =
              parsedData.topicCount || parsedData.topics.length;
            const limitedTopics = parsedData.topics.slice(0, topicLimit);

            const topicsDict = limitedTopics.reduce((acc, topic, index) => {
              acc[`topic-${index + 1}`] = {
                id: `topic-${index + 1}`,
                title: topic,
                status: "pending",
                postStatus: "not_started",
                createdAt: Date.now() + index, // Thêm createdAt để sort
              };
              return acc;
            }, {});
            setTopics(topicsDict);
          }

          if (parsedData.schedule) {
            // Cập nhật thời gian từ mảng times
            if (
              parsedData.schedule.times &&
              Array.isArray(parsedData.schedule.times)
            ) {
              // Lọc ra các thời gian mới chưa tồn tại
              const newTimes = parsedData.schedule.times.filter(
                (time) => !selectedTimes.includes(time),
              );
              // Thêm các thời gian mới vào mảng hiện có
              if (newTimes.length > 0) {
                setSelectedTimes([...selectedTimes, ...newTimes]);
              }
            }
            // Hỗ trợ cả định dạng cũ time
            else if (parsedData.schedule.time) {
              if (!selectedTimes.includes(parsedData.schedule.time)) {
                setSelectedTimes([...selectedTimes, parsedData.schedule.time]);
              }
            }

            // Cập nhật ngày
            if (
              parsedData.schedule.days &&
              parsedData.schedule.days.length > 0
            ) {
              const newSelectedDays = {
                monday: false,
                tuesday: false,
                wednesday: false,
                thursday: false,
                friday: false,
                saturday: false,
                sunday: false,
              };

              parsedData.schedule.days.forEach((day) => {
                if (newSelectedDays.hasOwnProperty(day.toLowerCase())) {
                  newSelectedDays[day.toLowerCase()] = true;
                }
              });

              setSelectedDays(newSelectedDays);
            }

            // Cập nhật độ dài bài viết
            if (parsedData.schedule.postLength) {
              setPostLength(parsedData.schedule.postLength);
            }

            // Cập nhật phong cách bài viết
            if (parsedData.schedule.postStyle) {
              const styleArray = parsedData.schedule.postStyle.split(", ");
              setPostStyles(styleArray);
            }

            // Không cần tìm fanpage phù hợp vì chức năng này không dùng đến
          }

          toast.success("Đã phân tích yêu cầu thành công!", {
            position: "top-right",
            autoClose: 3000,
          });
        } catch (error) {
          console.error("Error parsing JSON:", error);
          toast.error("Không thể phân tích yêu cầu. Vui lòng thử lại!", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      })
      .catch((error) => {
        console.error("Failed to analyze request:", error);
        toast.error(
          `Phân tích yêu cầu thất bại: ${aiRequestErrorMessage(error)}`,
          {
            position: "top-right",
            autoClose: 5000,
          },
        );
      })
      .finally(() => {
        setIsAnalyzingRequest(false);
      });
  };

  // Thêm hàm capitalizeFirstLetter
  const capitalizeFirstLetter = (string) => {
    if (!string) return string;
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Thêm useEffect để khởi tạo Web Speech API
  useEffect(() => {
    if ("webkitSpeechRecognition" in window) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "vi-VN";

      recognition.onresult = (event) => {
        const lastResult = event.results[event.results.length - 1];

        if (lastResult.isFinal) {
          let finalTranscript = capitalizeFirstLetter(
            lastResult[0].transcript.trim(),
          );

          // Thêm dấu chấm nếu câu chưa có dấu câu kết thúc
          const lastChar = finalTranscript.slice(-1);
          if (![".", "!", "?"].includes(lastChar)) {
            finalTranscript += "."; // Đảm bảo thêm dấu chấm câu
          }

          setTempTranscript("");

          setRequirements((prevRequirements) => {
            const prevText = prevRequirements.trim();
            if (!prevText) {
              return finalTranscript;
            }
            // Kiểm tra xem câu trước có dấu câu kết thúc chưa
            const lastCharPrev = prevText.slice(-1);
            if ([".", "!", "?"].includes(lastCharPrev)) {
              return `${prevText} ${finalTranscript}`;
            }
            return `${prevText}. ${finalTranscript}`;
          });
        } else {
          const interimTranscript = capitalizeFirstLetter(
            lastResult[0].transcript.trim(),
          );
          setTempTranscript(interimTranscript);
        }
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsRecording(false);
        setTempTranscript("");
      };

      recognition.onend = () => {
        setIsRecording(false);
        setTempTranscript("");
      };

      setRecognition(recognition);
    }
  }, []);

  // Thêm hàm xử lý bắt đầu/dừng ghi âm
  const toggleRecording = () => {
    if (!recognition) {
      toast.error(
        "Trình duyệt của bạn không hỗ trợ nhập liệu bằng giọng nói!",
        {
          position: "top-right",
          autoClose: 3000,
        },
      );
      return;
    }

    if (isRecording) {
      recognition.stop();
      setIsRecording(false);
      setTempTranscript("");
    } else {
      recognition.start();
      setIsRecording(true);
    }
  };

  // Thêm hàm xử lý clear text
  const clearText = () => {
    setRequirements("");
    setTempTranscript("");
  };

  // Thêm hàm xử lý allowNoImage
  const handleAllowNoImageChange = () => {
    const newValue = !allowNoImage;
    setAllowNoImage(newValue);
    // Nếu bật chế độ không ảnh, set imageSource = none
    const newImageSource = newValue ? IMAGE_SOURCES.NONE : DEFAULT_IMAGE_SOURCE;
    setImageSource(newImageSource);
    const newSettings = {
      ...settings,
      allowNoImage: newValue,
      imageSource: newImageSource,
    };
    saveUserSettings(newSettings);
  };

  // Xử lý thay đổi nguồn ảnh
  const handleImageSourceChange = (source) => {
    // Block AI source nếu chưa có gói Cao Cấp
    if (source === IMAGE_SOURCES.AI && userMembership < 3) {
      setShowUpgradeModal(true);
      return;
    }
    const nextAspectRatio = resolveAspectRatioByContext({
      aspectRatio,
      platform,
      inputType: wordpressInputType,
      imageSource: source,
    });
    setImageSource(source);
    const newAllowNoImage = source === IMAGE_SOURCES.NONE;
    setAllowNoImage(newAllowNoImage);
    setAspectRatio(nextAspectRatio);
    // Reset wpImageCount khi đổi nguồn ảnh: AI luôn cố định 2, none = 0
    const nextWpImageCount =
      source === IMAGE_SOURCES.AI
        ? WP_AI_IMAGE_COUNT
        : source === IMAGE_SOURCES.NONE
          ? 0
          : wpImageCount < DEFAULT_WP_IMAGE_COUNT
            ? DEFAULT_WP_IMAGE_COUNT
            : wpImageCount;
    setWpImageCount(nextWpImageCount);
    // fbImageCount chỉ áp dụng khi nguồn là Upload; nguồn khác không cần
    const nextFbImageCount =
      source === IMAGE_SOURCES.UPLOAD
        ? Math.max(fbImageCount, DEFAULT_FB_IMAGE_COUNT)
        : DEFAULT_FB_IMAGE_COUNT;
    setFbImageCount(nextFbImageCount);
    const newSettings = {
      ...settings,
      imageSource: source,
      allowNoImage: newAllowNoImage,
      aspectRatio: nextAspectRatio,
      wpImageCount: nextWpImageCount,
      fbImageCount: nextFbImageCount,
    };
    saveUserSettings(newSettings);
  };

  // Xử lý thay đổi số lượng ảnh cho bài viết WordPress
  // Xử lý thay đổi số ảnh/bài dùng chung cho WordPress (2-5) và Facebook (1-5) khi Upload
  // Reset uploadImageCursor về 0 khi user đổi số ảnh để tránh lệch vòng wrap-around
  const handleImageCountChange = (count) => {
    const isWordPress = platform === "wordpress";
    const prev = isWordPress ? wpImageCount : fbImageCount;
    if (imageSource === IMAGE_SOURCES.UPLOAD && count !== prev) {
      // Reset cursor khi số ảnh/bài thay đổi để giữ vòng lặp consistent
      config.uploadImageCursor = 0;
    }
    if (isWordPress) {
      setWpImageCount(count);
      saveUserSettings({ ...settings, wpImageCount: count });
    } else if (platform === "facebook") {
      setFbImageCount(count);
      saveUserSettings({ ...settings, fbImageCount: count });
    }
  };

  // Giữ lại alias cũ để tương thích các chỗ gọi cũ (nếu có)
  const handleWpImageCountChange = handleImageCountChange;

  // Xử lý thay đổi danh sách ảnh upload
  // Dùng useCallback để tránh tạo reference mới mỗi lần AutoPostGenerator re-render,
  // ngăn UserImageUploader re-render không cần thiết khi upload đang chạy.
  const handleUploadedImagesChange = useCallback((images) => {
    setUploadedImages(images);
  }, []);

  // ===== DEFERRED UPLOAD HELPERS =====
  // Convert File object sang base64 string để gửi API upload
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  /**
   * Upload tất cả ảnh local (chưa upload Cloudinary) lên Cloudinary.
   * Trả về mảng images đã cập nhật URL Cloudinary.
   * Ảnh đã có URL Cloudinary (edit mode) → giữ nguyên, không upload lại.
   */
  const uploadPendingImages = async (imagesToUpload) => {
    const userId = localStorage.getItem("userId");
    const results = [];

    for (const img of imagesToUpload) {
      if (!img._isLocal || !img._file) {
        // Ảnh đã upload Cloudinary rồi → giữ nguyên
        results.push({
          url: img.url,
          filename: img.filename,
          order: img.order,
        });
        continue;
      }

      // Convert file to base64 → upload lên Cloudinary
      const base64Data = await fileToBase64(img._file);
      const response = await apiClient.post("/api/content/upload-auto-post-image", {
        userId,
        imageData: base64Data,
      });

      if (response.data.success) {
        // Revoke blob URL của ảnh local sau khi upload thành công
        if (img.url && img.url.startsWith("blob:")) {
          URL.revokeObjectURL(img.url);
        }
        results.push({
          url: response.data.imageUrl,
          filename: img.filename,
          order: img.order,
        });
      } else {
        throw new Error(`Upload ảnh "${img.filename}" thất bại: ${response.data.message || "Lỗi không xác định"}`);
      }
    }

    return results;
  };

  // Xử lý thay đổi image settings
  const handleImageStyleChange = (style) => {
    setImageStyle(style);
    saveUserSettings({ ...settings, imageStyle: style });
  };

  const handleAspectRatioChange = (ratio) => {
    setAspectRatio(ratio);
    saveUserSettings({ ...settings, aspectRatio: ratio });
  };

  useEffect(() => {
    if (
      isWebsiteTopicMode &&
      imageSource === IMAGE_SOURCES.AI &&
      (aspectRatio === "16:9" ||
        aspectRatio === DEFAULT_ASPECT_RATIO ||
        !aspectRatio)
    ) {
      handleAspectRatioChange(WEBSITE_TOPIC_ASPECT_RATIO);
    }
  }, [isWebsiteTopicMode, imageSource, aspectRatio]);

  const handleCustomImagePromptChange = (e) => {
    setCustomImagePrompt(e.target.value);
  };

  const handleCustomImagePromptBlur = () => {
    saveUserSettings({ ...settings, customImagePrompt });
  };

  // Thêm hàm xử lý customInfo
  const handleCustomInfoChange = (e) => {
    const newValue = e.target.value;
    setCustomInfo(newValue);
    const newSettings = {
      ...settings,
      customInfo: newValue,
    };
    saveUserSettings(newSettings);
  };

  // Xử lý thay đổi nền tảng đăng bài
  const handlePlatformChange = (newPlatform) => {
    // Kiểm tra membership hoặc websitePostQuantity khi chọn WordPress
    if (newPlatform === "wordpress") {
      // Tất cả các gói đều trừ lượt hoặc điểm, không có miễn phí không giới hạn
      if (hasFreeWebsiteQuota || userMembership >= 3) {
        // Cho phép sử dụng
        if (hasFreeWebsiteQuota) {
          toast.info(
            `Bạn còn ${websitePostQuantity} lượt đăng bài lên website`,
            {
              position: "top-right",
              autoClose: 5000,
            },
          );
        }
      } else {
        // Không đủ điều kiện sử dụng
        toast.error(
          "Bạn cần có gói Cao cấp hoặc có lượt đăng bài website để sử dụng tính năng này",
          {
            position: "top-right",
            autoClose: 3000,
          },
        );
        return;
      }
    }

    setPlatform(newPlatform);
    setSelectedFanpages([]);
    if (newPlatform === "zalo") {
      setPostMode("direct");
    }

    // Set default post length based on platform
    const defaultPostLength =
      newPlatform === "wordpress"
        ? DEFAULT_WORDPRESS_POST_LENGTH // "Trung bình" for WordPress
        : DEFAULT_POST_LENGTH; // "150 - 250 từ" for Facebook

    setPostLength(defaultPostLength);

    // Always use topic mode - no need to set inputType
    const nextInputType =
      newPlatform === "facebook"
        ? facebookInputType
        : newPlatform === "zalo"
          ? zaloInputType
          : wordpressInputType;
    const nextAspectRatio = resolveAspectRatioByContext({
      aspectRatio,
      platform: newPlatform,
      inputType: nextInputType,
      imageSource,
    });
    const nextAllowProductLinks = resolveAllowProductLinksForContext({
      platform: newPlatform,
      inputType: nextInputType,
      allowProductLinks: allowWooProductLinks,
    });
    setAspectRatio(nextAspectRatio);
    setAllowWooProductLinks(nextAllowProductLinks);

    const newSettings = {
      ...settings,
      platform: newPlatform,
      inputType: "topic", // Always use topic mode
      wordpressInputType,
      facebookInputType,
      zaloInputType,
      allowProductLinks: nextAllowProductLinks,
      ...(newPlatform === "zalo" && {
        postMode: "direct",
        firstCommentEnabled: false,
        firstCommentMode: "manual",
        defaultFirstCommentText: "",
        firstCommentPrompt: "",
      }),
      postLength: defaultPostLength, // Update post length in settings
      aspectRatio: nextAspectRatio,
    };
    saveUserSettings(newSettings);
  };

  // Xử lý thay đổi cấu hình WordPress
  const handleWordpressConfigChange = (field, value) => {
    setWordpressConfig((prev) => {
      const normalizedValue =
        field === "categories" ? sanitizeWordpressCategoryIds(value) : value;
      const updated = { ...prev, [field]: normalizedValue };
      // Cập nhật settings
      const newSettings = {
        ...settings,
        wordpressConfig: updated,
      };
      saveUserSettings(newSettings);
      return updated;
    });
  };

  // Xử lý thay đổi trạng thái bài viết WordPress
  const handleWordpressStatusChange = (status) => {
    handleWordpressConfigChange("status", status);
  };

  // Xử lý thay đổi danh mục WordPress
  const handleCategorySelect = (category) => {
    const normalizedCategoryId = Number(category?.id);
    if (!Number.isInteger(normalizedCategoryId) || normalizedCategoryId <= 0) {
      return;
    }

    const currentCategories = sanitizeWordpressCategoryIds(
      wordpressConfig.categories,
    );
    const updatedCategories = currentCategories.includes(normalizedCategoryId)
      ? currentCategories.filter((id) => id !== normalizedCategoryId)
      : [...currentCategories, normalizedCategoryId];

    handleWordpressConfigChange("categories", updatedCategories);
  };

  // Hàm kiểm tra kết nối WordPress
  const testWordpressConnection = () => {
    if (!selectedWordpressSite) {
      toast.error("Vui lòng chọn một website WordPress!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsTestingWordpress(true);

    // Lấy thông tin từ selectedWordpressSite thay vì wordpressConfig
    const url = selectedWordpressSite.url;
    const username = selectedWordpressSite.username;
    const password = selectedWordpressSite.password;

    // TODO: Add actual WordPress authentication test here
    // Giả lập gọi API để kiểm tra kết nối đến WordPress
    setTimeout(() => {
      toast.success("Kết nối WordPress thành công!", {
        position: "top-right",
        autoClose: 3000,
      });
      setIsTestingWordpress(false);

      // Giả lập fetch categories - sẽ được thay thế bằng API thực tế
      setAvailableCategories([
        { id: 1, name: "Tin tức" },
        { id: 2, name: "Sản phẩm" },
        { id: 3, name: "Khuyến mãi" },
        { id: 4, name: "Công nghệ" },
      ]);
    }, 2000);
  };

  const handleSelectWordpressSite = (site) => {
    setSelectedWordpressSite(site);

    const baseUrl = normalizeWordPressSiteUrl(site.url);

    setWordpressConfig({
      url: baseUrl,
      username: site.username,
      password: site.password,
      categories: sanitizeWordpressCategoryIds(wordpressConfig.categories),
      tags: wordpressConfig.tags || "",
      status: wordpressConfig.status || "publish",
    });
    setIsWordpressSiteDropdownOpen(false);
    resetWooProductsState(true);

    // Fetch categories from WordPress
    fetchCategoriesFromWordPress(site.id);
  };

  // Fetch categories from WordPress site
  const fetchCategoriesFromWordPress = async (siteId) => {
    const userId = localStorage.getItem("userId");
    if (!userId || !siteId) return;

    setIsLoadingCategories(true);
    setCategoriesError("");

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/wordpress/categories/${siteId}`,
        { params: { userId } },
      );

      if (response.data.success) {
        setAvailableCategories(sanitizeWordpressCategories(response.data.data));
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategoriesError("Không thể tải danh mục từ WordPress");
    } finally {
      setIsLoadingCategories(false);
    }
  };

  // Cập nhật useEffect fetch WordPress sites để xử lý cả config cũ và mới
  useEffect(() => {
    const fetchWordpressSites = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const db = getDatabase();
        const wordpressSitesRef = ref(db, `users/${userId}/wordpressSites`);
        const wordpressSitesSnapshot = await get(wordpressSitesRef);

        if (wordpressSitesSnapshot.exists()) {
          const sitesData = wordpressSitesSnapshot.val();
          const sitesArray = Object.keys(sitesData).map((key) => ({
            id: key,
            ...sitesData[key],
          }));
          setWordpressSites(sitesArray);

          let siteSelectedFromConfig = null;

          if (config.id) {
            // Trường hợp 1: Config mới với wordpressSiteId
            if (config.wordpressSiteId) {
              const selectedSite = sitesArray.find(
                (site) => site.id === config.wordpressSiteId,
              );
              if (selectedSite) {
                siteSelectedFromConfig = selectedSite;
              }
            }
            // Trường hợp 2: Config cũ có wordpressUrl, username, password
            else if (config.wordpressUrl && config.wordpressUsername) {
              const normalizedConfigUrl = normalizeWordPressSiteUrl(
                config.wordpressUrl
              );

              // Kiểm tra xem URL đã tồn tại trong danh sách site chưa
              let matchedSite = sitesArray.find(
                (site) =>
                  normalizeWordPressSiteUrl(site.url) === normalizedConfigUrl,
              );

              if (matchedSite) {
                siteSelectedFromConfig = matchedSite;
                // Thông báo người dùng về việc chuyển đổi
                toast.info(
                  "Đã tự động cập nhật thông tin WordPress từ cấu hình cũ",
                  {
                    position: "top-right",
                    autoClose: 5000,
                  },
                );
              } else {
                // Không tìm thấy site phù hợp, thông báo người dùng cần chọn lại
                toast.warning(
                  "Cấu hình WordPress đã thay đổi, vui lòng chọn lại website",
                  {
                    position: "top-right",
                    autoClose: 5000,
                  },
                );
              }
            }
          }

          // Chọn site từ config nếu tìm thấy
          if (siteSelectedFromConfig) {
            setSelectedWordpressSite(siteSelectedFromConfig);
            // Cập nhật config WP không nhạy cảm từ config đã lưu
            setWordpressConfig((prevConfig) => ({
              ...prevConfig,
              categories: sanitizeWordpressCategoryIds(
                config.wordpressCategories,
              ),
              tags: config.wordpressTags || "",
              status: config.wordpressStatus || "publish",
            }));

            // Fetch categories để cho phép edit
            fetchCategoriesFromWordPress(siteSelectedFromConfig.id);
          }
          // Nếu không có site nào được chọn từ config VÀ chỉ có 1 site duy nhất
          else if (sitesArray.length === 1) {
            const baseUrl = normalizeWordPressSiteUrl(sitesArray[0].url);

            setSelectedWordpressSite(sitesArray[0]);
            setWordpressConfig((prevConfig) => ({
              ...prevConfig,
              url: baseUrl,
              username: sitesArray[0].username,
              password: sitesArray[0].password,
            }));
            fetchCategoriesFromWordPress(sitesArray[0].id);
          }
        } else {
          // Không có websites nào
          setWordpressSites([]);
        }
      } catch (error) {
        console.error("Error fetching WordPress sites:", error);
      }
    };

    // Gọi hàm để tải dữ liệu
    fetchWordpressSites();
  }, [location.pathname, config.id]);

  useEffect(() => {
    if (
      !isWooProductMode ||
      !selectedWordpressSite ||
      hasLoadedWooProducts ||
      isLoadingWooProducts
    ) {
      return;
    }

    fetchWooProductsForSite(selectedWordpressSite);
  }, [
    isWooProductMode,
    selectedWordpressSite,
    hasLoadedWooProducts,
    isLoadingWooProducts,
  ]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-5">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8 text-center">
          TẠO BÀI VIẾT TỰ ĐỘNG
        </h1>

        <PointWarning
          title="Vui lòng nạp thêm điểm để tiếp tục dùng chức năng này nhé ;)"
          userPoint={userPoint}
        />

        <div className="flex flex-col md:flex-row gap-6">
          {/* Phần bên trái - Cài đặt cơ bản */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-5 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  {config.fromProductsScreen
                    ? "Thiết lập đăng bài tự động từ sản phẩm"
                    : "Thiết lập tự động hóa"}
                </h2>
                <div className="flex items-center gap-2">
                  <a
                    href={
                      isWordpressProductMode
                        ? "https://youtu.be/U55K1ibNzhI"
                        : platform === "facebook" || platform === "zalo"
                          ? "https://youtu.be/boqoD2jfisY"
                          : "https://youtu.be/S_gCdSX4JJw"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium rounded-md shadow-sm transition duration-300 hover:text-red-600"
                    title="Xem video hướng dẫn cách sử dụng"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-red-600"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                    </svg>
                    <span>Hướng dẫn</span>
                  </a>
                </div>
              </div>

              {/* Thông báo khi từ ProductsScreen */}
              {config.fromProductsScreen && (
                <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                  <div className="flex items-center">
                    <FiFacebook className="text-blue-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">
                        Chế độ thiết lập từ sản phẩm - Facebook
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        Fanpage: {config.fanpageName || "Đã chọn"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Thêm lựa chọn nền tảng đăng bài - Ẩn khi từ ProductsScreen */}
              {!config.fromProductsScreen && (
                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">
                    Nền tảng đăng bài
                  </label>
                  <div className="flex p-1 space-x-1 bg-gray-100 rounded-lg shadow-inner">
                    <button
                      type="button"
                      className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 flex items-center justify-center ${
                        platform === "facebook"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      } ${!!config.id ? "opacity-50 cursor-not-allowed" : ""}`}
                      onClick={() => handlePlatformChange("facebook")}
                      disabled={!!config.id} // Vô hiệu hóa khi ở chế độ edit
                    >
                      <FiFacebook className="mr-2" />
                      Facebook
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 flex items-center justify-center ${
                        platform === "zalo"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      } ${!!config.id ? "opacity-50 cursor-not-allowed" : ""}`}
                      onClick={() => handlePlatformChange("zalo")}
                      disabled={!!config.id}
                    >
                      <span className="mr-2 font-bold">Z</span>
                      Zalo OA
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 flex items-center justify-center ${
                        platform === "wordpress"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      } ${
                        !!config.id ||
                        (userMembership < 3 && !hasFreeWebsiteQuota)
                          ? "opacity-50 cursor-pointer"
                          : ""
                      }`}
                      onClick={() => {
                        if (!!config.id) {
                          toast.info(
                            "Không thể thay đổi nền tảng khi chỉnh sửa thiết lập đã lưu",
                            {
                              position: "top-right",
                              autoClose: 3000,
                            },
                          );
                        } else if (
                          userMembership < 3 &&
                          !hasFreeWebsiteQuota
                        ) {
                          toast.error(
                            "Bạn cần có gói Cao cấp hoặc có lượt đăng bài website để sử dụng tính năng này",
                            {
                              position: "top-right",
                              autoClose: 3000,
                            },
                          );
                        } else {
                          handlePlatformChange("wordpress");
                        }
                      }}
                    >
                      <FiGlobe className="mr-2" />
                      Website
                    </button>
                  </div>
                </div>
              )}
              {/* Thông báo đã được chuyển vào onClick handler của button */}

              {(platform === "facebook" || platform === "zalo") && (
                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">
                    {platform === "zalo"
                      ? "Cách tạo nội dung cho Zalo OA"
                      : "Cách tạo nội dung cho fanpage"}
                  </label>
                  <div className="flex p-1 space-x-1 bg-gray-100 rounded-lg shadow-inner">
                    <button
                      type="button"
                      onClick={() =>
                        platform === "zalo"
                          ? handleZaloInputTypeChange("topic")
                          : handleFacebookInputTypeChange("topic")
                      }
                      className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 ${
                        activeProductInputType === "topic"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Từ chủ đề
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        platform === "zalo"
                          ? handleZaloInputTypeChange("products")
                          : handleFacebookInputTypeChange("products")
                      }
                      className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 ${
                        activeProductInputType === "products"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Từ sản phẩm WooCommerce
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    {isSocialWooProductMode
                      ? platform === "zalo"
                        ? "Mỗi lần tới lịch, hệ thống sẽ lấy lần lượt từng sản phẩm WooCommerce đã chọn để viết bài Zalo OA Article."
                        : "Mỗi lần tới lịch, hệ thống sẽ lấy lần lượt từng sản phẩm WooCommerce đã chọn để viết bài fanpage."
                      : platform === "zalo"
                        ? "Hệ thống sẽ tạo bài Zalo OA Article từ danh sách chủ đề bạn chuẩn bị sẵn."
                        : "Hệ thống sẽ tạo bài fanpage từ danh sách chủ đề bạn chuẩn bị sẵn."}
                  </p>
                </div>
              )}

              {/* Hiển thị số lượng lượt đăng bài website còn lại - chỉ áp dụng cho gói Trải nghiệm (6) */}
              {platform === "wordpress" &&
                (userMembership < 3 || userMembership === 6) && (
                  <div className="mt-2 mb-4">
                    <div
                      className={`p-3 rounded-md ${
                        hasFreeWebsiteQuota
                          ? "bg-green-50 border border-green-200"
                          : "bg-yellow-50 border border-yellow-200"
                      }`}
                    >
                      <p
                        className={`text-sm ${
                          hasFreeWebsiteQuota ? "text-green-700" : "text-yellow-700"
                        }`}
                      >
                        {hasFreeWebsiteQuota
                          ? `Bạn còn ${websitePostQuantity} lượt đăng bài lên website`
                          : config.id
                            ? "Bạn đã hết lượt đăng bài lên website. Vẫn có thể chỉnh sửa thiết lập hiện tại."
                            : userMembership === 6
                              ? "Bạn đã hết lượt đăng bài lên website. Vui lòng mua thêm lượt đăng bài hoặc nâng cấp gói."
                              : "Bạn đã hết lượt đăng bài lên website. Vui lòng nâng cấp lên gói Cao cấp hoặc mua thêm lượt đăng bài."}
                      </p>
                    </div>
                  </div>
                )}

              {/* WordPress input type selector */}
              {platform === "wordpress" && (
                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">
                    Cách tạo nội dung cho website
                  </label>
                  <div className="flex p-1 space-x-1 bg-gray-100 rounded-lg shadow-inner">
                    <button
                      type="button"
                      onClick={() => handleWordpressInputTypeChange("topic")}
                      className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 ${
                        wordpressInputType === "topic"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Từ chủ đề
                    </button>
                    <button
                      type="button"
                      onClick={() => handleWordpressInputTypeChange("products")}
                      className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 ${
                        wordpressInputType === "products"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Từ sản phẩm WooCommerce
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    {wordpressInputType === "products"
                      ? "Hệ thống sẽ lần lượt dùng từng sản phẩm bạn chọn để viết bài, ưu tiên ảnh sản phẩm và có thể chèn internal link thật."
                      : "Hệ thống sẽ tự tạo bài từ danh sách chủ đề bạn chuẩn bị sẵn."}
                  </p>
                </div>
              )}

              {isSocialWooProductMode && (
                <div className="space-y-6">
                  <div className="mb-6">
                    <label className="block text-gray-700 font-medium mb-2">
                      {platform === "zalo"
                        ? "Yêu cầu triển khai bài Zalo OA từ sản phẩm (không bắt buộc)"
                        : "Yêu cầu triển khai bài fanpage từ sản phẩm (không bắt buộc)"}
                    </label>
                    <textarea
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="3"
                      value={customInfo}
                      onChange={handleCustomInfoChange}
                      placeholder="Ví dụ: Viết giọng tư vấn gần gũi, nhấn mạnh lợi ích thực tế của sản phẩm, ưu tiên khách hàng nữ 25-35 tuổi, CTA nhẹ nhàng, tránh phóng đại..."
                    ></textarea>
                    <p className="text-sm text-gray-500 mt-1">
                      💡 AI sẽ dùng thông tin sản phẩm WooCommerce làm ngữ cảnh
                      chính, còn phần này để cá nhân hóa giọng viết và CTA.
                    </p>
                  </div>

                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-800 mb-3">
                      Nguồn sản phẩm WooCommerce
                    </h3>

                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-medium mb-1">
                        Chọn website
                      </label>
                      <div className="relative" ref={wordpressSiteDropdownRef}>
                        <button
                          type="button"
                          onClick={() =>
                            setIsWordpressSiteDropdownOpen(
                              !isWordpressSiteDropdownOpen,
                            )
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <span>
                            {selectedWordpressSite
                              ? selectedWordpressSite.siteName
                              : "Chọn website có WooCommerce"}
                          </span>
                          <svg
                            className="w-4 h-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>

                        {isWordpressSiteDropdownOpen && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            {wordpressSites.map((site) => (
                              <div
                                key={site.id}
                                className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                onClick={() => handleSelectWordpressSite(site)}
                              >
                                <span className="ml-2">{site.siteName}</span>
                                <span className="ml-2 text-xs text-gray-500">
                                  {site.url}
                                </span>
                              </div>
                            ))}
                            {wordpressSites.length === 0 && (
                              <div className="px-4 py-2 text-gray-500">
                                Không có website nào. Vui lòng thêm website
                                trong mục Thiết lập thông tin.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {wordpressSites.length === 0 && (
                        <p className="mt-2 text-sm text-blue-600">
                          <a href="/profile" className="underline">
                            Nhấn vào đây
                          </a>{" "}
                          để thêm website WordPress mới
                        </p>
                      )}
                    </div>

                    {selectedWordpressSite && (
                      <>
                        <div className="mt-4">
                          <div className="p-3 bg-blue-50 rounded-md">
                            <p className="text-sm text-gray-700 mb-1">
                              <span className="font-medium">Website:</span>{" "}
                              {selectedWordpressSite.siteName}
                            </p>
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">URL:</span>{" "}
                              {selectedWordpressSite.url}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 p-4 border border-amber-200 bg-amber-50 rounded-md">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                            <div className="text-sm text-amber-900">
                              <div className="flex flex-wrap items-center gap-2 font-medium">
                                <FiPackage size={16} />
                                <span>
                                  {selectedWooProducts.length > 0
                                    ? `${selectedWooProducts.length}/${totalFetchedWooProducts} sản phẩm đã chọn`
                                    : "Chưa chọn sản phẩm nào"}
                                </span>
                              </div>
                                <p className="mt-1 text-amber-800">
                                  Hệ thống sẽ lần lượt lấy từng sản phẩm trong
                                  danh sách này để tạo bài fanpage theo lịch.
                                </p>
                                {wooProductsMeta.fetchedAll &&
                                  totalFetchedWooProducts > 0 && (
                                    <p className="mt-1 text-amber-700">
                                      Hệ thống đã tải toàn bộ{" "}
                                      {totalFetchedWooProducts} sản phẩm từ
                                      website này.
                                    </p>
                                  )}
                              </div>
                            <div className="flex flex-wrap items-center gap-2">
                              {!isLoadingWooProducts &&
                                wooProducts.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={handleToggleSelectAllWooProducts}
                                    className={`inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium border ${
                                      allWooProductsSelected
                                        ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                                        : "border-blue-200 bg-white text-blue-700 hover:bg-blue-50"
                                    }`}
                                  >
                                    {allWooProductsSelected
                                      ? "Bỏ chọn tất cả"
                                      : "Chọn tất cả"}
                                  </button>
                                )}

                              <button
                                type="button"
                                onClick={() =>
                                  fetchWooProductsForSite(selectedWordpressSite)
                                }
                                disabled={isLoadingWooProducts}
                                className={`inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium ${
                                  isLoadingWooProducts
                                    ? "bg-gray-300 text-gray-700 cursor-not-allowed"
                                    : "bg-amber-600 text-white hover:bg-amber-700"
                                }`}
                              >
                                <FiRefreshCw
                                  className={`mr-2 ${
                                    isLoadingWooProducts ? "animate-spin" : ""
                                  }`}
                                  size={14}
                                />
                                {isLoadingWooProducts
                                  ? "Đang tải sản phẩm..."
                                  : hasLoadedWooProducts
                                    ? "Tải lại sản phẩm"
                                    : "Lấy danh sách sản phẩm"}
                              </button>
                            </div>
                          </div>

                          {hasLoadedWooProducts && wooProducts.length > 0 && (
                            <div className="relative mb-3">
                              <FiSearch
                                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                                size={18}
                                aria-hidden
                              />
                              <input
                                type="search"
                                value={wooProductSearchQuery}
                                onChange={(e) =>
                                  setWooProductSearchQuery(e.target.value)
                                }
                                placeholder="Tìm kiếm sản phẩm..."
                                className="w-full rounded-md border border-amber-200 bg-white py-2 pl-10 pr-3 text-sm text-gray-800 shadow-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                                autoComplete="off"
                                aria-label="Tìm kiếm sản phẩm"
                              />
                            </div>
                          )}

                          {wooProductsError && (
                            <div className="mb-3 p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-700">
                              {wooProductsError}
                            </div>
                          )}

                          {!isLoadingWooProducts &&
                            hasLoadedWooProducts &&
                            selectedWooProductIds.length > 0 &&
                            selectedWooProducts.length === 0 && (
                              <div className="mb-3 p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-700">
                                Các sản phẩm đã lưu không còn hợp lệ trên
                                website này. Vui lòng chọn lại trước khi lưu.
                              </div>
                            )}

                          {isLoadingWooProducts && (
                            <div className="text-sm text-gray-600">
                              Đang tải danh sách sản phẩm WooCommerce từ
                              website...
                            </div>
                          )}

                          {!isLoadingWooProducts &&
                            hasLoadedWooProducts &&
                            wooProducts.length === 0 &&
                            !wooProductsError && (
                              <div className="p-3 border border-dashed border-amber-300 rounded-md text-sm text-amber-900 bg-white">
                                Không tìm thấy sản phẩm WooCommerce public trên
                                website này.
                              </div>
                            )}

                          {!isLoadingWooProducts && wooProducts.length > 0 && (
                            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                              {filteredWooProducts.length === 0 && (
                                <div className="p-3 rounded-md border border-amber-200 bg-white text-sm text-amber-900">
                                  Không có sản phẩm nào khớp với từ khóa tìm
                                  kiếm.
                                </div>
                              )}
                              {filteredWooProducts.map((product) => {
                                const isSelected = selectedWooProductIds.some(
                                  (id) => String(id) === String(product.id),
                                );
                                const isPosted = isWooProductPosted(product);

                                return (
                                  <label
                                    key={product.id}
                                    className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                      isSelected
                                        ? "border-blue-300 bg-blue-50"
                                        : "border-gray-200 bg-white hover:bg-gray-50"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() =>
                                        handleWooProductSelectionChange(
                                          product.id,
                                        )
                                      }
                                      className="mt-1 h-4 w-4 text-blue-600 rounded"
                                    />

                                    {product.image?.thumbnail ||
                                    product.image?.src ? (
                                      <img
                                        src={
                                          product.image.thumbnail ||
                                          product.image.src
                                        }
                                        alt={product.image.alt || product.name}
                                        className="w-16 h-16 rounded-md object-cover border border-gray-200 bg-white"
                                      />
                                    ) : (
                                      <div className="w-16 h-16 rounded-md border border-dashed border-gray-300 bg-gray-100 flex items-center justify-center text-gray-400">
                                        <FiPackage size={20} />
                                      </div>
                                    )}

                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                        <p className="font-medium text-gray-800">
                                          {product.name}
                                        </p>
                                        {product.priceText && (
                                          <span className="inline-flex items-center text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                                            {product.priceText}
                                          </span>
                                        )}
                                      </div>

                                      {product.shortDescription && (
                                        <p className="mt-1 text-sm text-gray-600">
                                          {product.shortDescription}
                                        </p>
                                      )}

                                      {product.permalink && (
                                        <a
                                          href={product.permalink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="mt-2 inline-block text-xs text-blue-600 hover:text-blue-800 break-all"
                                          onClick={(event) =>
                                            event.stopPropagation()
                                          }
                                        >
                                          {product.permalink}
                                        </a>
                                      )}

                                      {isPosted && (
                                        <div className="mt-2 flex flex-wrap items-center gap-2">
                                          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                            Đã đăng
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {!isSocialWooProductMode &&
                (inputType === "requirement" ? (
                  <div className="mb-6">
                    <label className="block text-gray-700 font-medium mb-2">
                      Nhập yêu cầu bài viết
                    </label>
                    <textarea
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="4"
                      value={requirements}
                      onChange={(e) => setRequirements(e.target.value)}
                      placeholder="Nhập yêu cầu bài viết..."
                    ></textarea>
                  </div>
                ) : inputType === "topic" ? (
                  <div className="mb-6">
                    {/* Custom Info Field */}
                    <div className="mt-4">
                      <label className="block text-gray-700 font-medium mb-2">
                        {isWordpressProductMode
                          ? "Yêu cầu triển khai bài viết từ sản phẩm (không bắt buộc)"
                          : "Yêu cầu cá nhân hóa nội dung (không bắt buộc)"}
                      </label>
                      <textarea
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="3"
                        value={customInfo}
                        onChange={handleCustomInfoChange}
                        placeholder={
                          isWordpressProductMode
                            ? "Ví dụ: Viết theo giọng tư vấn chuyên gia, nhấn mạnh lợi ích thực tế của sản phẩm, ưu tiên khách hàng nữ 25-35 tuổi, CTA nhẹ nhàng, tránh nói quá đà..."
                            : "Nhập yêu cầu để cá nhân hóa nội dung bài viết. Ví dụ: Xưng hô tôi/bạn, nhắm đến khách hàng nữ 25-35 tuổi, nhấn mạnh lợi ích sức khỏe, tránh dùng từ ngữ quá chuyên môn, thêm câu hỏi tương tác cuối bài..."
                        }
                      ></textarea>
                      <p className="text-sm text-gray-500 mt-1">
                        💡 Mẹo: Mô tả cách xưng hô, đối tượng mục tiêu, điểm
                        nhấn nội dung hoặc các yêu cầu đặc biệt khác để AI triển
                        khai đúng ý.
                      </p>
                    </div>

                    {/* Nguồn ảnh cho bài viết */}
                    {!isWordpressProductMode &&
                      (platform === "facebook" || platform === "zalo" || platform === "wordpress") && (
                        <div className="mt-4 mb-4 p-3 bg-gray-50 rounded-md">
                          <label className="block text-gray-700 font-medium mb-2">
                            Nguồn ảnh cho bài viết
                          </label>
                          <div className="space-y-2">
                            {Object.entries(IMAGE_SOURCE_LABELS).map(
                              ([value, label]) => {
                                const isAILocked =
                                  value === IMAGE_SOURCES.AI &&
                                  userMembership < 3;
                                return (
                                  <label
                                    key={value}
                                    className={`flex items-center p-2 rounded-md transition-colors duration-150 cursor-pointer ${
                                      imageSource === value
                                        ? "bg-blue-50 border border-blue-300"
                                        : "hover:bg-gray-100"
                                    } ${isAILocked ? "opacity-75" : ""}`}
                                    onClick={(e) => {
                                      if (isAILocked) {
                                        e.preventDefault();
                                        handleImageSourceChange(value);
                                      }
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      name="imageSource"
                                      value={value}
                                      checked={imageSource === value}
                                      onChange={() =>
                                        !isAILocked && handleImageSourceChange(value)
                                      }
                                      className="mr-3 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                    />
                                    <span className="text-gray-700">
                                      {label}
                                    </span>
                                    {isAILocked && (
                                      <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded-full">
                                        🔒 Cao Cấp
                                      </span>
                                    )}
                                  </label>
                                );
                              },
                            )}
                          </div>
                          {/* Upload image list - hiển thị khi chọn nguồn upload */}
                          {imageSource === IMAGE_SOURCES.UPLOAD && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <UserImageUploader
                                images={uploadedImages}
                                onImagesChange={handleUploadedImagesChange}
                                maxImages={UPLOAD_IMAGE_MAX_COUNT}
                                maxSizeBytes={UPLOAD_IMAGE_MAX_SIZE_BYTES}
                                disabled={false}
                                uploadImageCursor={config.uploadImageCursor || 0}
                                isEditMode={!!config.id}
                              />
                            </div>
                          )}
                          {/* Số lượng ảnh - WordPress (2-5) hoặc Facebook (1-5) khi Upload */}
                          {(platform === "wordpress" || platform === "facebook") &&
                            imageSource === IMAGE_SOURCES.UPLOAD && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <label className="block text-sm text-gray-600 mb-2">
                                  📷 Số ảnh dùng cho mỗi bài viết
                                </label>
                                <div className="flex gap-2">
                                  {(platform === "wordpress" ? WP_IMAGE_COUNT_OPTIONS : FB_IMAGE_COUNT_OPTIONS).map((count) => {
                                    return (
                                      <button
                                        key={`img-count-${count}`}
                                        onClick={() => handleImageCountChange(count)}
                                        className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${
                                          (platform === "wordpress" ? wpImageCount : fbImageCount) === count
                                            ? "bg-blue-100 border border-blue-400 text-blue-700"
                                            : "bg-white border border-gray-300 hover:bg-gray-50 text-gray-600"
                                        }`}
                                      >
                                        {count} ảnh
                                      </button>
                                    );
                                  })}
                                </div>
                                {uploadedImages.length > 0 && uploadedImages.length < (platform === "wordpress" ? wpImageCount : fbImageCount) && (
                                  <p className="text-xs text-amber-600 mt-1">
                                    ⚠️ Bạn upload {uploadedImages.length} ảnh nhưng chọn {platform === "wordpress" ? wpImageCount : fbImageCount} ảnh/bài. Hệ thống sẽ dùng tối đa số ảnh đã upload.
                                  </p>
                                )}
                                <p className="text-xs text-gray-400 mt-1">
                                  2 bài viết liền kề sẽ không dùng ảnh trùng nhau
                                </p>
                              </div>
                            )}
                          {imageSource === IMAGE_SOURCES.AI && (
                            <div className="mt-3 space-y-3 pl-2 border-l-2 border-blue-200">
                              {/* Style selector - button+dropdown pattern */}
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">
                                  Phong cách ảnh
                                </label>
                                <div
                                  className="relative"
                                  ref={styleDropdownRef}
                                >
                                  <button
                                    onClick={() =>
                                      setIsStyleDropdownOpen(
                                        !isStyleDropdownOpen,
                                      )
                                    }
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center bg-white text-sm"
                                  >
                                    <span>
                                      {IMAGE_STYLE_OPTIONS.find(
                                        (opt) => opt.value === imageStyle,
                                      )?.label || "Chọn phong cách"}
                                    </span>
                                    <svg
                                      className={`w-4 h-4 transition-transform ${
                                        isStyleDropdownOpen
                                          ? "rotate-180"
                                          : "rotate-0"
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M19 9l-7 7-7-7"
                                      ></path>
                                    </svg>
                                  </button>
                                  {isStyleDropdownOpen && (
                                    <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg max-h-60 overflow-auto">
                                      <div className="flex flex-col">
                                        {IMAGE_STYLE_OPTIONS.map((opt) => (
                                          <button
                                            key={opt.value}
                                            onClick={() => {
                                              handleImageStyleChange(opt.value);
                                              setIsStyleDropdownOpen(false);
                                            }}
                                            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 border-b border-gray-100 last:border-b-0 ${
                                              imageStyle === opt.value
                                                ? "bg-blue-50 text-blue-700 font-medium"
                                                : ""
                                            }`}
                                          >
                                            {opt.label}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Aspect ratio */}
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">
                                  Tỷ lệ ảnh
                                </label>
                                <div className="flex gap-2">
                                  {aspectRatioOptions.map((opt) => (
                                    <label
                                      key={opt.value}
                                      className={`flex-1 text-center p-2 rounded-md cursor-pointer text-sm transition-colors ${
                                        aspectRatio === opt.value
                                          ? "bg-blue-100 border border-blue-400 text-blue-700"
                                          : "bg-white border border-gray-300 hover:bg-gray-50"
                                      }`}
                                    >
                                      <input
                                        type="radio"
                                        name="aspectRatio"
                                        value={opt.value}
                                        checked={aspectRatio === opt.value}
                                        onChange={() =>
                                          handleAspectRatioChange(opt.value)
                                        }
                                        className="hidden"
                                      />
                                      {opt.label}
                                      <span className="block text-xs text-gray-500">
                                        {opt.desc}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              {/* Custom prompt */}
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">
                                  Yêu cầu thêm (tùy chọn)
                                </label>
                                <textarea
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  rows="2"
                                  value={customImagePrompt}
                                  onChange={handleCustomImagePromptChange}
                                  onBlur={handleCustomImagePromptBlur}
                                  maxLength={500}
                                  placeholder="VD: tone ấm, có cây xanh, phong cách vintage..."
                                ></textarea>
                                <p className="text-xs text-gray-500 mt-1">
                                  💡 AI sẽ tự tạo mô tả chi tiết từ yêu cầu của
                                  bạn
                                </p>
                              </div>
                            </div>
                          )}
                          {imageSource === IMAGE_SOURCES.INTERNET &&
                            [2, 3, 4].includes(userMembership) && (
                              <div className="mt-3 pl-2 border-l-2 border-gray-200">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-600">
                                    Kiểm duyệt ảnh
                                  </span>
                                  <div className="relative inline-block align-middle select-none">
                                    <input
                                      type="checkbox"
                                      name="imageValidation"
                                      id="imageValidation"
                                      checked={imageValidationEnabled}
                                      onChange={handleImageValidationChange}
                                      className="hidden"
                                    />
                                    <label
                                      htmlFor="imageValidation"
                                      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                                      style={{
                                        backgroundColor: imageValidationEnabled
                                          ? "#3b82f6"
                                          : "#e5e7eb",
                                      }}
                                    >
                                      <span
                                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                                          imageValidationEnabled
                                            ? "translate-x-6"
                                            : "translate-x-1"
                                        }`}
                                      />
                                    </label>
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  AI tự động loại bỏ ảnh chứa logo, số điện
                                  thoại
                                </p>
                              </div>
                            )}
                        </div>
                      )}

                    {/* Số lượng ảnh sản phẩm — chỉ hiển thị cho WordPress Product mode */}
                    {isWordpressProductMode && platform === "wordpress" && (
                      <div className="mt-4 mb-4 p-3 bg-gray-50 rounded-md">
                        <label className="block text-gray-700 font-medium mb-2">
                          📷 Số lượng ảnh sản phẩm tối đa trong bài viết
                        </label>
                        <div className="flex gap-2">
                          {WP_IMAGE_COUNT_OPTIONS.map((count) => (
                            <button
                              key={`wp-product-img-count-${count}`}
                              onClick={() =>
                                handleWpImageCountChange(count)
                              }
                              className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${
                                wpImageCount === count
                                  ? "bg-blue-100 border border-blue-400 text-blue-700"
                                  : "bg-white border border-gray-300 hover:bg-gray-50 text-gray-600"
                              }`}
                            >
                              {count} ảnh
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          Nếu sản phẩm có ít hơn {wpImageCount} ảnh, hệ thống sẽ
                          dùng tất cả ảnh có sẵn của sản phẩm.
                        </p>
                      </div>
                    )}

                    {!isWordpressProductMode ? (
                      <>
                        <label className="block text-gray-700 font-medium mb-2">
                          Nhập yêu cầu để tạo chủ đề
                        </label>
                        <div className="relative">
                          <textarea
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium text-gray-700"
                            rows="4"
                            value={requirements}
                            onChange={(e) => setRequirements(e.target.value)}
                            placeholder="Nhập yêu cầu chủ đề. Ví dụ: du lịch Đà Nẵng, ẩm thực Đà Nẵng,.."
                          ></textarea>
                          {requirements && (
                            <button
                              onClick={() => setRequirements("")}
                              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                              title="Xóa nội dung"
                            >
                              <FiXCircle size={20} />
                            </button>
                          )}
                          <div className="flex justify-between items-center mt-1 text-xs">
                            <span className="text-gray-400">
                              Gợi ý: Cung cấp chủ đề chính để AI gợi ý danh sách bài viết đa dạng.
                            </span>
                            <span className={`font-semibold ${
                              requirements.length > 8000
                                ? "text-red-500"
                                : requirements.length > 6000
                                ? "text-orange-500"
                                : "text-gray-400"
                            }`}>
                              {requirements.length} / 8000 ký tự
                            </span>
                          </div>
                        </div>

                        <div className="mt-4 mb-2">
                          <label className="block text-gray-700 font-medium mb-2">
                            Số lượng chủ đề
                          </label>
                          <div className="flex gap-2.5 flex-wrap items-center">
                            <div
                              className="relative w-36 flex-shrink-0"
                              ref={topicCountDropdownRef}
                            >
                              <button
                                onClick={() =>
                                  setIsTopicCountDropdownOpen(
                                    !isTopicCountDropdownOpen,
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center bg-white text-sm"
                              >
                                <span>{topicCount} chủ đề</span>
                                <svg
                                  className={`w-4 h-4 transition-transform ${
                                    isTopicCountDropdownOpen
                                      ? "rotate-180"
                                      : "rotate-0"
                                  }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M19 9l-7 7-7-7"
                                  ></path>
                                </svg>
                              </button>
                              {isTopicCountDropdownOpen && (
                                <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg max-h-60 overflow-auto">
                                  <div className="flex flex-col">
                                    {[10, 15, 20, 25, 30, 60, 90].map((count) => (
                                      <button
                                        key={count}
                                        onClick={() =>
                                          handleTopicCountChange(count)
                                        }
                                        className="w-full px-4 py-2 text-left hover:bg-gray-100 border-b border-gray-100 last:border-b-0 text-sm"
                                      >
                                        {count} chủ đề
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            {Object.keys(topics).length > 0 ? (
                              <>
                                <button
                                  onClick={() =>
                                    platform === "facebook" || platform === "zalo"
                                      ? generateFacebookTopics(true)
                                      : generateWordpressTopics(true)
                                  }
                                  className={`w-28 flex-shrink-0 flex items-center justify-center bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-medium ${
                                    isGeneratingTopics || userPoint <= 0
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                  disabled={isGeneratingTopics || userPoint <= 0}
                                  title="Gợi ý thêm chủ đề mới cộng dồn vào danh sách hiện tại"
                                >
                                  {isGeneratingTopics && (
                                    <FiRefreshCw
                                      className="mr-1 h-3 w-3 animate-spin"
                                    />
                                  )}
                                  Tạo thêm
                                </button>

                                <button
                                  onClick={() =>
                                    platform === "facebook" || platform === "zalo"
                                      ? generateFacebookTopics(false)
                                      : generateWordpressTopics(false)
                                  }
                                  className={`w-28 flex-shrink-0 flex items-center justify-center border border-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-medium ${
                                    isGeneratingTopics || userPoint <= 0
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                  disabled={isGeneratingTopics || userPoint <= 0}
                                  title="Xóa danh sách hiện tại để tạo danh sách mới hoàn toàn"
                                >
                                  Tạo mới
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() =>
                                  platform === "facebook" || platform === "zalo"
                                    ? generateFacebookTopics(false)
                                    : generateWordpressTopics(false)
                                }
                                className={`w-32 flex-shrink-0 flex items-center justify-center bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-medium ${
                                  isGeneratingTopics || userPoint <= 0
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                }`}
                                disabled={isGeneratingTopics || userPoint <= 0}
                              >
                                {isGeneratingTopics && (
                                  <FiRefreshCw
                                    className="mr-2 h-4 w-4 animate-spin"
                                  />
                                )}
                                {isGeneratingTopics
                                  ? "Đang tạo..."
                                  : userPoint <= 0
                                    ? "Không đủ điểm"
                                    : `Tạo chủ đề`}
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 mb-2">
                          <label className="block text-gray-700 font-medium mb-2">
                            Thêm chủ đề thủ công
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Nhập chủ đề bạn muốn thêm..."
                              value={manualTopic}
                              onChange={(e) => setManualTopic(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  handleAddManualTopic();
                                }
                              }}
                            />
                            <button
                              onClick={handleAddManualTopic}
                              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 font-medium"
                            >
                              <div className="flex items-center">
                                <span className="mr-1">+</span> Thêm
                              </div>
                            </button>
                          </div>
                        </div>

                        {Object.keys(topics).length > 0 && (
                          <div className="mt-4">
                            <div className="flex items-center justify-between gap-3 mb-2">
                              <h3 className="text-gray-700 font-medium">
                                Danh sách chủ đề:
                              </h3>
                              <div className="flex items-center gap-2">
                                {showExhaustedLabel && activeProductInputType !== "products" && (
                                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                                    Đã đăng hết
                                  </span>
                                )}
                                <button
                                  onClick={clearAllTopics}
                                  className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                                >
                                  <FiTrash2 size={14} /> Xóa tất cả
                                </button>
                              </div>
                            </div>
                            <ul className="list-disc list-inside">
                              {Object.values(topics)
                                .sort((a, b) => {
                                  const timeA = a.createdAt || 0;
                                  const timeB = b.createdAt || 0;
                                  if (timeA !== timeB) {
                                    return timeA - timeB;
                                  }
                                  const getNumericIndex = (item) => {
                                    if (item.id && String(item.id).startsWith("topic-")) {
                                      const num = parseInt(String(item.id).replace("topic-", ""), 10);
                                      if (!isNaN(num)) return num;
                                    }
                                    return 999999;
                                  };
                                  return getNumericIndex(a) - getNumericIndex(b);
                                })
                                .map((topic, index) => (
                                  <li
                                    key={topic.id}
                                    className="flex justify-between items-center py-1"
                                  >
                                    <div className="flex items-center justify-between w-full">
                                      <div className="flex items-center">
                                        <button
                                          onClick={() => deleteTopic(topic.id)}
                                          className="mr-2 text-red-500 hover:text-red-700"
                                        >
                                          <FiTrash2 size={18} />
                                        </button>
                                        <span>
                                          {index + 1}. {topic.title}
                                        </span>
                                      </div>
                                      <div className="ml-2">
                                        {topic.postStatus === "posted" && (
                                          <FiCheck
                                            className="text-green-500"
                                            size={16}
                                          />
                                        )}
                                        {topic.postStatus === "failed" && (
                                          <FiX
                                            className="text-red-500"
                                            size={16}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </li>
                                ))}
                            </ul>
                            {config.id && (
                              <button
                                onClick={resetTopicsStatus}
                                className="mt-4 flex items-center justify-center w-full px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                              >
                                <FiRefreshCw className="mr-2" />
                                Reset trạng thái đăng bài
                              </button>
                            )}
                          </div>
                        )}

                        {activeProductInputType === "products" &&
                          hasSelectedWooProducts && (
                            <div className="mt-4">
                              <div className="flex items-center justify-between gap-3 mb-2">
                                <h3 className="text-gray-700 font-medium">
                                  Trạng thái sản phẩm:
                                </h3>
                                {showExhaustedLabel && (
                                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                                    Đã đăng hết
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">
                                {selectedWooProductsToDisplay.filter((product) =>
                                  isWooProductPosted(product),
                                ).length}
                                /
                                {selectedWooProductsToDisplay.length} sản phẩm đã
                                đăng
                              </p>
                            </div>
                          )}
                      </>
                    ) : null}
                  </div>
                ) : inputType === "quickCreate" ? (
                  <div className="mb-6">
                    <label className="block text-gray-700 font-medium mb-2">
                      Nhập yêu cầu để phân tích tự động
                    </label>
                    <div className="relative">
                      <textarea
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="4"
                        value={`${requirements}${
                          tempTranscript ? ` ${tempTranscript}` : ""
                        }`}
                        onChange={(e) => setRequirements(e.target.value)}
                        placeholder="Nhập yêu cầu chi tiết. Ví dụ: Tôi muốn hằng ngày vào 8h sáng bạn sẽ viết 1 bài viết ngắn theo hướng chuyên môn về chủ đề ẩm thực Đà Nẵng để đăng lên fanpage AI - người bạn đồng hành của tôi."
                      ></textarea>
                      {(requirements || tempTranscript) && (
                        <button
                          onClick={clearText}
                          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                          title="Xóa nội dung"
                        >
                          <FiXCircle size={20} />
                        </button>
                      )}
                      <button
                        onClick={toggleRecording}
                        className={`absolute bottom-3 right-3 p-2 rounded-full ${
                          isRecording
                            ? "bg-red-500 hover:bg-red-600"
                            : "bg-blue-500 hover:bg-blue-600"
                        } text-white focus:outline-none`}
                        title={isRecording ? "Dừng ghi âm" : "Bắt đầu ghi âm"}
                      >
                        {isRecording ? (
                          <FiMicOff size={20} />
                        ) : (
                          <FiMic size={20} />
                        )}
                      </button>
                    </div>
                    {isRecording && (
                      <div className="mt-2 text-sm text-blue-600 animate-pulse">
                        Đang ghi âm... Hãy nói yêu cầu của bạn
                      </div>
                    )}

                    {/* Nguồn ảnh cho bài viết (Quick mode) */}
                    {(platform === "facebook" || platform === "zalo" || platform === "wordpress") && (
                      <div className="mt-4 mb-4 p-3 bg-gray-50 rounded-md">
                        <label className="block text-gray-700 font-medium mb-2">
                          Nguồn ảnh cho bài viết
                        </label>
                        <div className="space-y-2">
                          {Object.entries(IMAGE_SOURCE_LABELS).map(
                            ([value, label]) => {
                              const isAILocked =
                                value === IMAGE_SOURCES.AI &&
                                userMembership < 3;
                              return (
                                <label
                                  key={`quick-${value}`}
                                  className={`flex items-center p-2 rounded-md transition-colors duration-150 cursor-pointer ${
                                    imageSource === value
                                      ? "bg-blue-50 border border-blue-300"
                                      : "hover:bg-gray-100"
                                  } ${isAILocked ? "opacity-75" : ""}`}
                                  onClick={(e) => {
                                    if (isAILocked) {
                                      e.preventDefault();
                                      handleImageSourceChange(value);
                                    }
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name="imageSourceQuick"
                                    value={value}
                                    checked={imageSource === value}
                                    onChange={() =>
                                      !isAILocked && handleImageSourceChange(value)
                                    }
                                    className="mr-3 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                  <span className="text-gray-700">{label}</span>
                                  {isAILocked && (
                                    <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded-full">
                                      🔒 Cao Cấp
                                    </span>
                                  )}
                                </label>
                              );
                            },
                          )}
                        </div>
                        {/* Upload image list - Quick mode */}
                        {imageSource === IMAGE_SOURCES.UPLOAD && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <UserImageUploader
                              images={uploadedImages}
                              onImagesChange={handleUploadedImagesChange}
                              maxImages={UPLOAD_IMAGE_MAX_COUNT}
                              maxSizeBytes={UPLOAD_IMAGE_MAX_SIZE_BYTES}
                              disabled={false}
                              uploadImageCursor={config.uploadImageCursor || 0}
                              isEditMode={!!config.id}
                            />
                          </div>
                        )}
                        {/* Số lượng ảnh - WordPress (2-5) hoặc Facebook (1-5) khi Upload (Quick mode) */}
                        {(platform === "wordpress" || platform === "facebook") &&
                          imageSource === IMAGE_SOURCES.UPLOAD && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <label className="block text-sm text-gray-600 mb-2">
                                📷 Số ảnh dùng cho mỗi bài viết
                              </label>
                              <div className="flex gap-2">
                                {(platform === "wordpress" ? WP_IMAGE_COUNT_OPTIONS : FB_IMAGE_COUNT_OPTIONS).map((count) => {
                                  return (
                                    <button
                                      key={`quick-img-count-${count}`}
                                      onClick={() => handleImageCountChange(count)}
                                    className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${
                                      (platform === "wordpress" ? wpImageCount : fbImageCount) === count
                                        ? "bg-blue-100 border border-blue-400 text-blue-700"
                                        : "bg-white border border-gray-300 hover:bg-gray-50 text-gray-600"
                                    }`}
                                  >
                                    {count} ảnh
                                  </button>
                                );
                              })}
                            </div>
                            {uploadedImages.length > 0 && uploadedImages.length < (platform === "wordpress" ? wpImageCount : fbImageCount) && (
                              <p className="text-xs text-amber-600 mt-1">
                                ⚠️ Bạn upload {uploadedImages.length} ảnh nhưng chọn {platform === "wordpress" ? wpImageCount : fbImageCount} ảnh/bài. Hệ thống sẽ dùng tối đa số ảnh đã upload.
                              </p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              2 bài viết liền kề sẽ không dùng ảnh trùng nhau
                            </p>
                          </div>
                        )}
                        {imageSource === IMAGE_SOURCES.AI && (
                          <div className="mt-3 space-y-3 pl-2 border-l-2 border-blue-200">
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">
                                Phong cách ảnh
                              </label>
                              <div className="relative" ref={styleDropdownRef}>
                                <button
                                  onClick={() =>
                                    setIsStyleDropdownOpen(!isStyleDropdownOpen)
                                  }
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center bg-white text-sm"
                                >
                                  <span>
                                    {IMAGE_STYLE_OPTIONS.find(
                                      (opt) => opt.value === imageStyle,
                                    )?.label || "Chọn phong cách"}
                                  </span>
                                  <svg
                                    className={`w-4 h-4 transition-transform ${
                                      isStyleDropdownOpen
                                        ? "rotate-180"
                                        : "rotate-0"
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M19 9l-7 7-7-7"
                                    ></path>
                                  </svg>
                                </button>
                                {isStyleDropdownOpen && (
                                  <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg max-h-60 overflow-auto">
                                    <div className="flex flex-col">
                                      {IMAGE_STYLE_OPTIONS.map((opt) => (
                                        <button
                                          key={`quick-style-${opt.value}`}
                                          onClick={() => {
                                            handleImageStyleChange(opt.value);
                                            setIsStyleDropdownOpen(false);
                                          }}
                                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 border-b border-gray-100 last:border-b-0 ${
                                            imageStyle === opt.value
                                              ? "bg-blue-50 text-blue-700 font-medium"
                                              : ""
                                          }`}
                                        >
                                          {opt.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm text-gray-600 mb-1">
                                Tỷ lệ ảnh
                              </label>
                              <div className="flex gap-2">
                                {aspectRatioOptions.map((opt) => (
                                  <label
                                    key={`quick-ratio-${opt.value}`}
                                    className={`flex-1 text-center p-2 rounded-md cursor-pointer text-sm transition-colors ${
                                      aspectRatio === opt.value
                                        ? "bg-blue-100 border border-blue-400 text-blue-700"
                                        : "bg-white border border-gray-300 hover:bg-gray-50"
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="aspectRatioQuick"
                                      value={opt.value}
                                      checked={aspectRatio === opt.value}
                                      onChange={() =>
                                        handleAspectRatioChange(opt.value)
                                      }
                                      className="hidden"
                                    />
                                    {opt.label}
                                    <span className="block text-xs text-gray-500">
                                      {opt.desc}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm text-gray-600 mb-1">
                                Yêu cầu thêm (tùy chọn)
                              </label>
                              <textarea
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                rows="2"
                                value={customImagePrompt}
                                onChange={handleCustomImagePromptChange}
                                onBlur={handleCustomImagePromptBlur}
                                maxLength={500}
                                placeholder="VD: tone ấm, có cây xanh, phong cách vintage..."
                              ></textarea>
                              <p className="text-xs text-gray-500 mt-1">
                                💡 AI sẽ tự tạo mô tả chi tiết từ yêu cầu của
                                bạn
                              </p>
                            </div>
                          </div>
                        )}
                        {imageSource === IMAGE_SOURCES.INTERNET &&
                          [2, 3, 4].includes(userMembership) && (
                            <div className="mt-3 pl-2 border-l-2 border-gray-200">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">
                                  Kiểm duyệt ảnh
                                </span>
                                <div className="relative inline-block align-middle select-none">
                                  <input
                                    type="checkbox"
                                    name="imageValidationQuick"
                                    id="imageValidationQuick"
                                    checked={imageValidationEnabled}
                                    onChange={handleImageValidationChange}
                                    className="hidden"
                                  />
                                  <label
                                    htmlFor="imageValidationQuick"
                                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                                    style={{
                                      backgroundColor: imageValidationEnabled
                                        ? "#3b82f6"
                                        : "#e5e7eb",
                                    }}
                                  >
                                    <span
                                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                                        imageValidationEnabled
                                          ? "translate-x-6"
                                          : "translate-x-1"
                                      }`}
                                    />
                                  </label>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                AI tự động loại bỏ ảnh chứa logo, số điện thoại
                              </p>
                            </div>
                          )}
                      </div>
                    )}

                    <button
                      onClick={analyzeRequest}
                      className={`flex items-center justify-center w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mt-4 text-sm sm:text-base font-medium ${
                        isAnalyzingRequest || userPoint <= 0
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      disabled={isAnalyzingRequest || userPoint <= 0}
                    >
                      {isAnalyzingRequest && (
                        <FiRefreshCw
                          className={`mr-2 h-5 w-5 ${
                            isAnalyzingRequest ? "animate-spin" : ""
                          }`}
                        />
                      )}
                      {isAnalyzingRequest
                        ? "Đang phân tích yêu cầu..."
                        : userPoint <= 0
                          ? "Không đủ point"
                          : "Phân tích yêu cầu"}
                    </button>
                  </div>
                ) : (
                  <div className="mb-6">
                    <div className="text-blue-700 mb-4">
                      Nội dung bài viết sẽ được tạo tự động dựa trên danh sách
                      sản phẩm có sẵn
                    </div>
                    <button
                      onClick={() => navigate("/product-create")}
                      className="inline-flex items-center justify-center bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm sm:text-base font-medium"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Thêm sản phẩm mới
                    </button>

                    {/* Custom Info Field for Products */}
                    <div className="mt-4">
                      <label className="block text-gray-700 font-medium mb-2">
                        Yêu cầu cá nhân hóa nội dung (không bắt buộc)
                      </label>
                      <textarea
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="3"
                        value={customInfo}
                        onChange={handleCustomInfoChange}
                        placeholder="Nhập yêu cầu để cá nhân hóa nội dung bài viết. Ví dụ: Xưng hô tôi/bạn, nhắm đến khách hàng nữ 25-35 tuổi, nhấn mạnh lợi ích sức khỏe, tránh dùng từ ngữ quá chuyên môn, thêm câu hỏi tương tác cuối bài..."
                      ></textarea>
                      <p className="text-sm text-gray-500 mt-1">
                        💡 Mẹo: Mô tả về cách xưng hô, đối tượng khách hàng mục
                        tiêu, điểm nhấn nội dung, hoặc các yêu cầu đặc biệt khác
                      </p>
                    </div>
                  </div>
                ))}

              {/* Phần cấu hình WordPress */}
              {platform === "wordpress" && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-3">
                    Cấu hình WordPress
                  </h3>

                  <div className="space-y-4">
                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-medium mb-1">
                        Chọn website
                      </label>
                      <div className="relative" ref={wordpressSiteDropdownRef}>
                        <button
                          type="button"
                          onClick={() =>
                            setIsWordpressSiteDropdownOpen(
                              !isWordpressSiteDropdownOpen,
                            )
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <span>
                            {selectedWordpressSite
                              ? selectedWordpressSite.siteName
                              : "Chọn website WordPress"}
                          </span>
                          <svg
                            className="w-4 h-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>

                        {isWordpressSiteDropdownOpen && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            {wordpressSites.map((site) => (
                              <div
                                key={site.id}
                                className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                onClick={() => handleSelectWordpressSite(site)}
                              >
                                <span className="ml-2">{site.siteName}</span>
                                <span className="ml-2 text-xs text-gray-500">
                                  {site.url}
                                </span>
                              </div>
                            ))}
                            {wordpressSites.length === 0 && (
                              <div className="px-4 py-2 text-gray-500">
                                Không có website nào. Vui lòng thêm website
                                trong mục Thiết lập thông tin.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {wordpressSites.length === 0 && (
                        <p className="mt-2 text-sm text-blue-600">
                          <a href="/profile" className="underline">
                            Nhấn vào đây
                          </a>{" "}
                          để thêm website WordPress mới
                        </p>
                      )}
                    </div>

                    {selectedWordpressSite && (
                      <>
                        <div className="mt-4">
                          <div className="p-3 bg-blue-50 rounded-md">
                            <p className="text-sm text-gray-700 mb-1">
                              <span className="font-medium">Website:</span>{" "}
                              {selectedWordpressSite.siteName}
                            </p>
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">URL:</span>{" "}
                              {selectedWordpressSite.url}
                            </p>
                          </div>
                        </div>

                        {isWordpressProductMode && (
                          <div className="mt-4 p-4 border border-amber-200 bg-amber-50 rounded-md">
                            <div className="flex items-start justify-between gap-3 mb-4 p-3 bg-white border border-amber-200 rounded-md">
                              <div>
                                <label className="text-sm font-medium text-gray-800">
                                  Cho phép AI chèn link sản phẩm
                                </label>
                                <p className="mt-1 text-sm text-gray-600">
                                  {allowWooProductLinks
                                    ? "AI được phép chèn internal link sản phẩm thật vào bài viết."
                                    : "AI chỉ dùng dữ liệu sản phẩm làm ngữ cảnh và ưu tiên ảnh, không chèn link sản phẩm."}
                                </p>
                              </div>
                              <div className="relative inline-block align-middle select-none">
                                <input
                                  type="checkbox"
                                  name="allowWooProductLinks"
                                  id="allowWooProductLinks"
                                  checked={allowWooProductLinks}
                                  onChange={handleAllowWooProductLinksChange}
                                  className="hidden"
                                />
                                <label
                                  htmlFor="allowWooProductLinks"
                                  className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                                  style={{
                                    backgroundColor: allowWooProductLinks
                                      ? "#3b82f6"
                                      : "#e5e7eb",
                                  }}
                                >
                                  <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                                      allowWooProductLinks
                                        ? "translate-x-6"
                                        : "translate-x-1"
                                    }`}
                                  />
                                </label>
                              </div>
                            </div>

                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                              <div className="text-sm text-amber-900">
                                <div className="flex flex-wrap items-center gap-2 font-medium">
                                  <FiPackage size={16} />
                                  <span>
                                    {selectedWooProducts.length > 0
                                      ? `${selectedWooProducts.length}/${totalFetchedWooProducts} sản phẩm đã chọn`
                                      : "Chưa chọn sản phẩm nào"}
                                  </span>
                                  <span
                                    className="inline-flex items-center rounded-full border border-amber-200 bg-white px-2 py-0.5 text-xs font-medium text-amber-800 cursor-help"
                                    title={
                                      allowWooProductLinks
                                        ? "Mỗi lần đến lịch, hệ thống sẽ lấy lần lượt từng sản phẩm đã chọn để tạo bài. Ảnh minh họa dùng ảnh sản phẩm thật từ website và AI có thể chèn internal link sản phẩm nếu phù hợp."
                                        : "Mỗi lần đến lịch, hệ thống sẽ lấy lần lượt từng sản phẩm đã chọn để tạo bài. Ảnh minh họa dùng ảnh sản phẩm thật từ website và bài viết sẽ không chèn link sản phẩm."
                                    }
                                  >
                                    Cách hoạt động
                                  </span>
                                </div>
                                <p className="mt-1 text-amber-800">
                                  {selectedWooProducts.length > 0
                                    ? "Hệ thống sẽ luân phiên tạo bài trực tiếp từ danh sách sản phẩm bạn đã chọn."
                                    : "Chọn ít nhất 1 sản phẩm để hệ thống bắt đầu luân phiên tạo bài tự động."}
                                </p>
                                {wooProductsMeta.fetchedAll &&
                                  totalFetchedWooProducts > 0 && (
                                    <p className="mt-1 text-amber-700">
                                      Hệ thống đã tải toàn bộ{" "}
                                      {totalFetchedWooProducts} sản phẩm từ
                                      website này.
                                    </p>
                                  )}
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                {!isLoadingWooProducts &&
                                  wooProducts.length > 0 && (
                                    <button
                                      type="button"
                                      onClick={handleToggleSelectAllWooProducts}
                                      className={`inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium border ${
                                        allWooProductsSelected
                                          ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                                          : "border-blue-200 bg-white text-blue-700 hover:bg-blue-50"
                                      }`}
                                    >
                                      {allWooProductsSelected
                                        ? "Bỏ chọn tất cả"
                                        : "Chọn tất cả"}
                                    </button>
                                  )}

                                <button
                                  type="button"
                                  onClick={() =>
                                    fetchWooProductsForSite(
                                      selectedWordpressSite,
                                    )
                                  }
                                  disabled={isLoadingWooProducts}
                                  className={`inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium ${
                                    isLoadingWooProducts
                                      ? "bg-gray-300 text-gray-700 cursor-not-allowed"
                                      : "bg-amber-600 text-white hover:bg-amber-700"
                                  }`}
                                >
                                  <FiRefreshCw
                                    className={`mr-2 ${
                                      isLoadingWooProducts ? "animate-spin" : ""
                                    }`}
                                    size={14}
                                  />
                                  {isLoadingWooProducts
                                    ? "Đang tải sản phẩm..."
                                    : hasLoadedWooProducts
                                      ? "Tải lại sản phẩm"
                                      : "Lấy danh sách sản phẩm"}
                                </button>
                              </div>
                            </div>

                            {hasLoadedWooProducts && wooProducts.length > 0 && (
                              <div className="relative mb-3">
                                <FiSearch
                                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                                  size={18}
                                  aria-hidden
                                />
                                <input
                                  type="search"
                                  value={wooProductSearchQuery}
                                  onChange={(e) =>
                                    setWooProductSearchQuery(e.target.value)
                                  }
                                  placeholder="Tìm kiếm sản phẩm..."
                                  className="w-full rounded-md border border-amber-200 bg-white py-2 pl-10 pr-3 text-sm text-gray-800 shadow-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                                  autoComplete="off"
                                  aria-label="Tìm kiếm sản phẩm"
                                />
                              </div>
                            )}

                            {wooProductsError && (
                              <div className="mb-3 p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-700">
                                {wooProductsError}
                              </div>
                            )}

                            {!isLoadingWooProducts &&
                              hasLoadedWooProducts &&
                              selectedWooProductIds.length > 0 &&
                              selectedWooProducts.length === 0 && (
                                <div className="mb-3 p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-700">
                                  Các sản phẩm đã lưu không còn hợp lệ trên
                                  website này. Vui lòng chọn lại sản phẩm trước
                                  khi lưu.
                                </div>
                              )}

                            {isLoadingWooProducts && (
                              <div className="text-sm text-gray-600">
                                Đang tải danh sách sản phẩm WooCommerce từ
                                website...
                              </div>
                            )}

                            {!isLoadingWooProducts &&
                              hasLoadedWooProducts &&
                              wooProducts.length === 0 &&
                              !wooProductsError && (
                                <div className="p-3 border border-dashed border-amber-300 rounded-md text-sm text-amber-900 bg-white">
                                  Không tìm thấy sản phẩm WooCommerce public
                                  trên website này.
                                </div>
                              )}

                            {!isLoadingWooProducts &&
                              wooProducts.length > 0 && (
                                <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                                  {filteredWooProducts.length === 0 && (
                                    <div className="p-3 rounded-md border border-amber-200 bg-white text-sm text-amber-900">
                                      Không có sản phẩm nào khớp với từ khóa
                                      tìm kiếm.
                                    </div>
                                  )}
                                  {filteredWooProducts.map((product) => {
                                    const isSelected =
                                      selectedWooProductIds.some(
                                        (id) =>
                                          String(id) === String(product.id),
                                      );
                                    const isPosted =
                                      isWooProductPosted(product);

                                    return (
                                      <label
                                        key={product.id}
                                        className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                          isSelected
                                            ? "border-blue-300 bg-blue-50"
                                            : "border-gray-200 bg-white hover:bg-gray-50"
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() =>
                                            handleWooProductSelectionChange(
                                              product.id,
                                            )
                                          }
                                          className="mt-1 h-4 w-4 text-blue-600 rounded"
                                        />

                                        {product.image?.thumbnail ||
                                        product.image?.src ? (
                                          <img
                                            src={
                                              product.image.thumbnail ||
                                              product.image.src
                                            }
                                            alt={
                                              product.image.alt || product.name
                                            }
                                            className="w-16 h-16 rounded-md object-cover border border-gray-200 bg-white"
                                          />
                                        ) : (
                                          <div className="w-16 h-16 rounded-md border border-dashed border-gray-300 bg-gray-100 flex items-center justify-center text-gray-400">
                                            <FiPackage size={20} />
                                          </div>
                                        )}

                                        <div className="min-w-0 flex-1">
                                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                            <p className="font-medium text-gray-800">
                                              {product.name}
                                            </p>
                                            {product.priceText && (
                                              <span className="inline-flex items-center text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                                                {product.priceText}
                                              </span>
                                            )}
                                          </div>

                                          {product.shortDescription && (
                                            <p className="mt-1 text-sm text-gray-600">
                                              {product.shortDescription}
                                            </p>
                                          )}

                                          <a
                                            href={product.permalink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-2 inline-block text-xs text-blue-600 hover:text-blue-800 break-all"
                                          >
                                            {product.permalink}
                                          </a>

                                          {isPosted && (
                                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                              {isPosted && (
                                                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                                  Đã đăng
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                          </div>
                        )}

                        <div className="mt-4">
                          <label className="block text-gray-700 text-sm font-medium mb-2">
                            Trạng thái bài viết
                          </label>
                          <div className="flex gap-3">
                            <button
                              type="button"
                              className={`px-4 py-2 rounded-md text-sm font-medium ${
                                wordpressConfig.status === "draft"
                                  ? "bg-blue-600 text-white"
                                  : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                              }`}
                              onClick={() =>
                                handleWordpressStatusChange("draft")
                              }
                            >
                              Bản nháp
                            </button>
                            <button
                              type="button"
                              className={`px-4 py-2 rounded-md text-sm font-medium ${
                                wordpressConfig.status === "publish"
                                  ? "bg-blue-600 text-white"
                                  : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                              }`}
                              onClick={() =>
                                handleWordpressStatusChange("publish")
                              }
                            >
                              Công khai
                            </button>
                          </div>
                        </div>

                        {/* Category selection section */}
                        <div className="mt-4">
                          {/* Header với toggle switch */}
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-gray-700 text-sm font-medium">
                              Danh mục bài viết
                            </label>
                            <div className="relative inline-block w-12 align-middle select-none">
                              <input
                                type="checkbox"
                                name="categoryToggleAuto"
                                id="categoryToggleAuto"
                                checked={categoryMode !== "disabled"}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setCategoryMode("manual"); // Bật -> default là manual
                                    // Reset categories về rỗng để dropdown hiển thị "Chọn danh mục"
                                    setWordpressConfig((prev) => ({
                                      ...prev,
                                      categories: [],
                                    }));
                                  } else {
                                    setCategoryMode("disabled"); // Tắt
                                  }
                                }}
                                className="hidden"
                              />
                              <label
                                htmlFor="categoryToggleAuto"
                                className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out
                                  ${
                                    categoryMode !== "disabled"
                                      ? "bg-blue-600"
                                      : "bg-gray-300"
                                  }`}
                              >
                                <span
                                  className={`block h-6 w-6 rounded-full shadow transform transition-transform duration-200 ease-in-out
                                    ${
                                      categoryMode !== "disabled"
                                        ? "translate-x-6 bg-white"
                                        : "translate-x-0 bg-white"
                                    }`}
                                />
                              </label>
                            </div>
                          </div>

                          {categoryMode === "disabled" ? (
                            <p className="text-sm text-gray-500">
                              Bật để chọn danh mục cho bài viết.
                            </p>
                          ) : (
                            <>
                              {/* Chế độ chọn danh mục: thủ công hoặc AI */}
                              <div className="flex w-full rounded-md shadow-sm mb-3">
                                {["manual", "ai"].map((mode, index, arr) => (
                                  <button
                                    key={mode}
                                    type="button"
                                    className={`flex-1 py-2 px-2 text-center text-sm font-medium
                                      ${index === 0 ? "rounded-l-md" : ""}
                                      ${
                                        index === arr.length - 1
                                          ? "rounded-r-md"
                                          : ""
                                      }
                                      ${
                                        categoryMode === mode
                                          ? "bg-blue-600 text-white z-10"
                                          : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                                      }`}
                                    onClick={() => setCategoryMode(mode)}
                                  >
                                    {mode === "manual"
                                      ? "Tự chọn danh mục"
                                      : "AI tự chọn danh mục"}
                                  </button>
                                ))}
                              </div>

                              {categoryMode === "ai" ? (
                                <p className="text-sm text-gray-500">
                                  AI sẽ tự chọn hoặc tạo danh mục phù hợp dựa
                                  trên nội dung bài viết.
                                </p>
                              ) : (
                                <>
                                  {isLoadingCategories ? (
                                    <div className="text-sm text-gray-500">
                                      Đang tải danh mục từ website WordPress...
                                    </div>
                                  ) : availableCategories.length > 0 ? (
                                    <div
                                      className="relative"
                                      ref={categoryDropdownRef}
                                    >
                                      <button
                                        type="button"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        onClick={() =>
                                          setIsCategoryDropdownOpen(
                                            !isCategoryDropdownOpen,
                                          )
                                        }
                                      >
                                        <span className="truncate">
                                          {wordpressConfig.categories.length ===
                                          0
                                            ? "Chọn danh mục"
                                            : (() => {
                                                const selectedCategories =
                                                  availableCategories.filter(
                                                    (cat) =>
                                                      wordpressConfig.categories.includes(
                                                        cat.id,
                                                      ),
                                                  );
                                                if (
                                                  selectedCategories.length ===
                                                  0
                                                ) {
                                                  return `${wordpressConfig.categories.length} danh mục đã chọn`;
                                                }
                                                const names =
                                                  selectedCategories.map(
                                                    (cat) => cat.name,
                                                  );
                                                if (names.length <= 2) {
                                                  return names.join(", ");
                                                }
                                                return `${names
                                                  .slice(0, 2)
                                                  .join(", ")} +${
                                                  names.length - 2
                                                }`;
                                              })()}
                                        </span>
                                        <svg
                                          className="w-4 h-4"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      </button>

                                      {isCategoryDropdownOpen && (
                                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                          {availableCategories.map(
                                            (category) => (
                                              <div
                                                key={category.id}
                                                className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                                onClick={() =>
                                                  handleCategorySelect(category)
                                                }
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={wordpressConfig.categories.includes(
                                                    category.id,
                                                  )}
                                                  onChange={() => {}}
                                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-2">
                                                  {category.name}
                                                </span>
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ) : categoriesError ? (
                                    <p className="text-sm text-red-500">
                                      {categoriesError}
                                    </p>
                                  ) : (
                                    <p className="text-sm text-gray-500">
                                      Vui lòng chọn website WordPress để tải
                                      danh mục.
                                    </p>
                                  )}
                                </>
                              )}

                              {/* Display selected categories (similar to fanpage selection) */}
                              {categoryMode === "manual" &&
                                wordpressConfig.categories.length > 0 &&
                                availableCategories.length > 0 && (
                                  <div className="mt-3">
                                    <label className="block text-gray-700 text-sm font-medium mb-2">
                                      Danh mục đã chọn (
                                      {wordpressConfig.categories.length}):
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                      {availableCategories
                                        .filter((cat) =>
                                          wordpressConfig.categories.includes(
                                            cat.id,
                                          ),
                                        )
                                        .map((category) => (
                                          <div
                                            key={category.id}
                                            className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full"
                                          >
                                            <span className="mr-2">
                                              {category.name}
                                            </span>
                                            <button
                                              onClick={() =>
                                                handleCategorySelect(category)
                                              }
                                              className="text-blue-600 hover:text-blue-800 focus:outline-none"
                                              title="Bỏ chọn danh mục này"
                                            >
                                              <FiX size={16} />
                                            </button>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                            </>
                          )}
                        </div>

                        {/* <div className="mt-4">
                          <label className="block text-gray-700 text-sm font-medium mb-2">
                            Thẻ (ngăn cách bằng dấu phẩy)
                          </label>
                          <input
                            type="text"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Thẻ1, Thẻ2, Thẻ3"
                            value={wordpressConfig.tags}
                            onChange={(e) =>
                              handleWordpressConfigChange(
                                "tags",
                                e.target.value
                              )
                            }
                          />
                        </div> */}

                        {/* <button
                          onClick={testWordpressConnection}
                          className={`mt-4 flex items-center justify-center bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-medium w-full ${
                            isTestingWordpress
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          disabled={isTestingWordpress}
                        >
                          {isTestingWordpress ? (
                            <>
                              <FiRefreshCw className="mr-2 animate-spin" />
                              Đang kiểm tra kết nối...
                            </>
                          ) : (
                            "Kiểm tra kết nối"
                          )}
                        </button> */}
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="mb-6">
                <PostLanguageSelector
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={handleLanguageChange}
                />
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Độ dài bài viết
                </label>
                <div className="flex w-full rounded-md shadow-sm">
                  {(platform === "wordpress"
                    ? WORDPRESS_POST_LENGTHS
                    : POST_LENGTHS
                  ).map(({ value, label }, index, arr) => (
                    <button
                      key={value}
                      type="button"
                      className={`flex-1 py-2 px-2 text-center text-sm font-medium
                        ${index === 0 ? "rounded-l-md" : ""}
                        ${index === arr.length - 1 ? "rounded-r-md" : ""}
                        ${
                          postLength === value
                            ? "bg-blue-600 text-white z-10"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                      onClick={() => setPostLength(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Phong cách bài viết
                </label>
                <div className="flex flex-wrap gap-2">
                  {POST_STYLES.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`py-2 px-4 rounded-full text-sm font-medium
                        ${
                          postStyles.includes(value)
                            ? "bg-blue-600 text-white"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                      onClick={() => handlePostStyleChange(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                {/* Hàng 1: Các nút toggle nhỏ (Dùng thông tin fanpage/website/Zalo OA & Thêm thông tin liên hệ) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {(platform !== "facebook" || selectedFanpages.length < 2) && (
                    <div className="p-3 border border-gray-200 rounded-md bg-white shadow-sm flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <label className="text-gray-700 font-medium text-sm sm:text-base">
                          {platform === "facebook"
                            ? "Dùng thông tin fanpage"
                            : platform === "zalo"
                            ? "Dùng thông tin Zalo OA"
                            : "Dùng thông tin website"}
                        </label>
                        {(platform === "facebook" || platform === "zalo") && (
                          <SettingTooltip
                            label={platform === "zalo" ? "Dùng thông tin Zalo OA" : "Dùng thông tin fanpage"}
                            text={FANPAGE_INFO_TOOLTIP_TEXT}
                          />
                        )}
                      </div>
                      <div className="relative inline-block align-middle select-none shrink-0">
                        <input
                          type="checkbox"
                          name="fanpageInfo"
                          id="fanpageInfo"
                          checked={useFanpageInfo}
                          onChange={handleUseFanpageInfoChange}
                          className="hidden"
                        />
                        <label
                          htmlFor="fanpageInfo"
                          className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                          style={{
                            backgroundColor: useFanpageInfo
                              ? "#3b82f6"
                              : "#e5e7eb",
                          }}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                              useFanpageInfo
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  {(platform === "facebook" || platform === "wordpress") && (
                    <div className="p-3 border border-gray-200 rounded-md bg-white shadow-sm flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <label className="text-gray-700 font-medium text-sm sm:text-base">
                          Thêm thông tin liên hệ
                        </label>
                        <SettingTooltip
                          label="Thêm thông tin liên hệ"
                          text={
                            platform === "wordpress"
                              ? WEBSITE_CONTACT_TOGGLE_TOOLTIP_TEXT
                              : CONTACT_INFO_TOOLTIP_TEXT
                          }
                        />
                      </div>
                      <div className="relative inline-block align-middle select-none shrink-0">
                        <input
                          type="checkbox"
                          name="contactInfo"
                          id="contactInfo"
                          checked={includeContactInfo}
                          onChange={handleIncludeContactInfoChange}
                          className="hidden"
                        />
                        <label
                          htmlFor="contactInfo"
                          className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                          style={{
                            backgroundColor: includeContactInfo
                              ? "#3b82f6"
                              : "#e5e7eb",
                          }}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                              includeContactInfo
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>



                {/* Hàng 2: Premium Grounding: Google Search opt-in hoặc Zalo OA info note */}
                {(platform === "facebook" || platform === "wordpress") && (
                  <div className="w-full">
                    <GroundingToggle
                      useGoogleSearch={useGoogleSearch}
                      onChange={handleUseGoogleSearchChange}
                      isPlanEligible={userMembership >= 3}
                      contextLabel="bài viết Facebook/WordPress"
                    />
                  </div>
                )}

                {platform === "zalo" && (
                  <div className="w-full p-3 border border-blue-100 rounded-md bg-blue-50">
                    <div className="flex items-start gap-2 text-xs text-blue-700">
                      <FiAlertTriangle className="mt-0.5 shrink-0" />
                      <span>
                        Tiêu đề ≤ 150 ký tự, Mô tả ≤ 300 ký tự. Bài viết cần ảnh cover HTTPS để đăng lên Zalo OA.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Phần bên phải - Cài đặt lịch trình */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-5 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Cài đặt lịch trình
                </h2>
              </div>

              {/* Schedule Mode Selector */}
              <div className="mb-6">
                <ScheduleModeSelector
                  value={scheduleMode}
                  onChange={setScheduleMode}
                  disabled={isSaving}
                />
              </div>

              {/* Weekly Mode Schedule */}
              {scheduleMode === "weekly" && (
                <div className="mb-6">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="w-full md:w-1/3 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                      <label className="block text-gray-700 font-medium mb-2">
                        Giờ đăng bài
                      </label>
                      <div className="space-y-3">
                        {selectedTimes.map((time, index) => {
                          const [hours, minutes] = time.split(":");
                          return (
                            <div
                              key={index}
                              className="flex items-center gap-2"
                            >
                              <div className="relative flex-1 flex shadow-sm rounded-md overflow-hidden border border-gray-200">
                                {/* Dropdown giờ */}
                                <div className="relative w-1/2">
                                  <select
                                    value={hours}
                                    onChange={(e) => {
                                      const newTimes = [...selectedTimes];
                                      const newHours = e.target.value;
                                      newTimes[index] =
                                        `${newHours}:${minutes}`;
                                      setSelectedTimes(newTimes);
                                    }}
                                    className="w-full h-full px-4 py-2.5 text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 border-none focus:outline-none appearance-none text-center font-medium"
                                  >
                                    {Array.from({ length: 24 }, (_, i) => {
                                      const hour = i < 10 ? `0${i}` : `${i}`;
                                      return (
                                        <option key={hour} value={hour}>
                                          {hour}
                                        </option>
                                      );
                                    })}
                                  </select>
                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                                    <svg
                                      className="w-4 h-4 fill-current"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                </div>

                                {/* Dấu hai chấm */}
                                <div className="flex items-center justify-center bg-gray-50 px-2">
                                  <span className="text-gray-500 font-medium">
                                    :
                                  </span>
                                </div>

                                {/* Dropdown phút */}
                                <div className="relative w-1/2">
                                  <select
                                    value={minutes}
                                    onChange={(e) => {
                                      const newTimes = [...selectedTimes];
                                      const newMinutes = e.target.value;
                                      newTimes[index] =
                                        `${hours}:${newMinutes}`;
                                      setSelectedTimes(newTimes);
                                    }}
                                    className="w-full h-full px-4 py-2.5 text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 border-none focus:outline-none appearance-none text-center font-medium"
                                  >
                                    {(() => {
                                      const isDev = process.env.NODE_ENV === 'development';
                                      const step = isDev ? 1 : 5;
                                      const count = isDev ? 60 : 12;
                                      return Array.from({ length: count }, (_, i) => {
                                        const minute = i * step;
                                        const formattedMinute =
                                          minute < 10
                                            ? `0${minute}`
                                            : `${minute}`;
                                        return (
                                          <option
                                            key={formattedMinute}
                                            value={formattedMinute}
                                          >
                                            {formattedMinute}
                                          </option>
                                        );
                                      });
                                    })()}
                                  </select>
                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                                    <svg
                                      className="w-4 h-4 fill-current"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const newTimes = selectedTimes.filter(
                                    (_, i) => i !== index,
                                  );
                                  setSelectedTimes(newTimes);
                                }}
                                className={`p-2 text-white ${
                                  selectedTimes.length > 1
                                    ? "bg-red-500 hover:bg-red-600"
                                    : "bg-gray-300 cursor-not-allowed"
                                } rounded-full transition-colors focus:outline-none ${
                                  selectedTimes.length > 1
                                    ? "focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                    : ""
                                }`}
                                aria-label="Xóa giờ đăng"
                                disabled={selectedTimes.length <= 1}
                                title={
                                  selectedTimes.length <= 1
                                    ? "Phải có ít nhất một giờ đăng bài"
                                    : "Xóa giờ đăng này"
                                }
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedTimes([...selectedTimes, "08:00"])
                          }
                          className="w-full px-4 py-2.5 bg-white text-blue-600 border border-blue-500 rounded-md hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center font-medium shadow-sm"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-2"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Thêm giờ đăng
                        </button>
                      </div>
                    </div>

                    <div className="w-full md:w-2/3 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                      <label className="block text-gray-700 font-medium mb-2">
                        Ngày đăng bài
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(daysInVietnamese).map(
                          ([day, label]) => (
                            <label
                              key={day}
                              className={`inline-flex items-center justify-center px-3 py-2 rounded-md cursor-pointer select-none transition-colors duration-200
                            ${
                              selectedDays[day]
                                ? "bg-blue-500 text-white"
                                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                            }`}
                            >
                              <input
                                type="checkbox"
                                className="hidden"
                                checked={selectedDays[day]}
                                onChange={() => handleDayChange(day)}
                              />
                              <span className="text-sm font-medium">
                                {label}
                              </span>
                            </label>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Fixed Dates Mode Schedule */}
              {scheduleMode === "fixed-dates" && (
                <div className="mb-6">
                  <FixedDateScheduleUI
                    schedules={fixedDateSchedules}
                    onAdd={handleAddFixedDateSchedule}
                    onEdit={handleEditFixedDateSchedule}
                    onDelete={handleDeleteFixedDateSchedule}
                    onToggleCompleted={handleToggleScheduleCompleted}
                  />
                </div>
              )}

              {/* Hiển thị phần chọn tài khoản cho Facebook/Zalo */}
              {(platform === "facebook" || platform === "zalo") && (
                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">
                    {platform === "zalo"
                      ? "Chọn Zalo OA để đăng bài"
                      : inputType === "products"
                      ? "Chọn Fanpage để đăng bài (chỉ 1 fanpage)"
                      : "Chọn Fanpage để đăng bài"}
                  </label>
                  {platform === "facebook" && inputType === "products" && (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-700">
                        <span className="font-medium">Lưu ý:</span> Chế độ tạo
                        từ sản phẩm chỉ cho phép chọn 1 fanpage vì mỗi sản phẩm
                        được thiết lập cho 1 fanpage cụ thể khi tạo.
                      </p>
                    </div>
                  )}
                  {availablePostingAccounts.length === 0 ? (
                    <button
                      onClick={() => {
                        navigate("/profile", {
                          state: { activeTab: platform === "zalo" ? "zalo" : "facebook" },
                        });
                        setTimeout(() => {
                          const element = document.getElementById(
                            platform === "zalo" ? "zalo-account-management" : "facebook-fanpage-management",
                          );
                          if (element) {
                            element.scrollIntoView({ behavior: "smooth" });
                          }
                        }, 500);
                      }}
                      className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm sm:text-base"
                    >
                      {platform === "zalo"
                        ? "Thiết lập Zalo OA để đăng bài"
                        : "Thiết lập fanpage để đăng bài"}
                    </button>
                  ) : (
                    <div ref={dropdownRef} className="relative">
                      <button
                        onClick={toggleDropdown}
                        className="w-full px-4 py-2 text-left border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {selectedFanpages.length > 0
                          ? selectedFanpages.length === 1
                            ? selectedFanpages[0].name
                            : `${selectedFanpages.length} tài khoản đã chọn`
                          : platform === "zalo"
                            ? "Chọn Zalo OA"
                            : "Chọn Fanpage"}
                      </button>
                      {isDropdownOpen && (
                        <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg">
                          {/* Header - Ẩn nút chọn tất cả khi inputType là products */}
                          <div className="border-b border-gray-200 px-4 py-2 bg-gray-50">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700 font-medium">
                                {inputType === "products"
                                  ? `Chọn ${platform === "zalo" ? "Zalo OA" : "fanpage"} (${selectedFanpages.length}/1)`
                                  : `Chọn tài khoản (${selectedFanpages.length}/${availablePostingAccounts.length})`}
                              </span>
                              {inputType !== "products" && (
                                <button
                                  onClick={() => {
                                    if (
                                      selectedFanpages.length ===
                                      availablePostingAccounts.length
                                    ) {
                                      // Nếu đã chọn tất cả thì bỏ chọn tất cả
                                      setSelectedFanpages([]);
                                    } else {
                                      // Nếu chưa chọn tất cả thì chọn tất cả
                                      setSelectedFanpages([...availablePostingAccounts]);
                                    }
                                  }}
                                  className="text-sm text-blue-600 hover:text-blue-800 font-medium focus:outline-none"
                                >
                                  {selectedFanpages.length === availablePostingAccounts.length
                                    ? "Bỏ chọn tất cả"
                                    : "Chọn tất cả"}
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Danh sách fanpages */}
                          <div className="max-h-48 overflow-y-auto">
                            {availablePostingAccounts.map((fanpage) => (
                              <div
                                key={getSocialAccountKey(fanpage)}
                                onClick={() => handleFanpageSelect(fanpage)}
                                className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between ${
                                  selectedFanpages.find(
                                    (fp) =>
                                      getSocialAccountKey(fp) ===
                                      getSocialAccountKey(fanpage),
                                  )
                                    ? "bg-blue-50"
                                    : ""
                                }`}
                              >
                                <div className="flex items-center flex-1">
                                  <input
                                    type="checkbox"
                                    checked={selectedFanpages.some(
                                      (fp) =>
                                        getSocialAccountKey(fp) ===
                                        getSocialAccountKey(fanpage),
                                    )}
                                    onChange={() => {}} // Handle by div onClick
                                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <span className="text-left truncate">
                                    {fanpage.name}
                                  </span>
                                  <span className="ml-2 text-xs text-gray-500 uppercase">
                                    {fanpage.platform === "zalo" ? "Zalo OA" : "Facebook"}
                                  </span>
                                </div>
                                {selectedFanpages.find(
                                  (fp) =>
                                    getSocialAccountKey(fp) ===
                                    getSocialAccountKey(fanpage),
                                ) && (
                                  <span className="text-blue-600 ml-2">✓</span>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Nút hoàn tất để đóng dropdown */}
                          <div className="border-t border-gray-200 px-4 py-3">
                            <button
                              onClick={() => setIsDropdownOpen(false)}
                              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-medium"
                            >
                              {inputType === "products"
                                ? `Hoàn tất (${selectedFanpages.length}/1 fanpage đã chọn)`
                                : `Hoàn tất (${
                                    selectedFanpages.length
                                  } tài khoản${
                                    selectedFanpages.length !== 1 ? "s" : ""
                                  } đã chọn)`}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Hiển thị danh sách fanpages đã chọn */}
                  {selectedFanpages.length > 0 && (
                    <div className="mt-3">
                      <label className="block text-gray-700 font-medium mb-2">
                        Tài khoản đã chọn ({selectedFanpages.length}):
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {selectedFanpages.map((fanpage) => (
                          <div
                            key={getSocialAccountKey(fanpage)}
                            className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full"
                          >
                            <span className="mr-2">{fanpage.name}</span>
                            <button
                              onClick={() => handleFanpageSelect(fanpage)}
                              className="text-blue-600 hover:text-blue-800 focus:outline-none"
                              title="Bỏ chọn fanpage này"
                            >
                              <FiX size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mb-6">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <label className="block text-gray-700 font-medium">
                    Tự động viết và đăng bài
                  </label>
                  <div className="relative inline-block align-middle select-none">
                    <input
                      type="checkbox"
                      name="autoPost"
                      id="autoPost"
                      checked={autoPostEnabled}
                      onChange={handleAutoPostToggle}
                      className="hidden"
                    />
                    <label
                      htmlFor="autoPost"
                      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                      style={{
                        backgroundColor: autoPostEnabled
                          ? "#3b82f6"
                          : "#e5e7eb",
                      }}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                          autoPostEnabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </label>
                  </div>
                </div>
                {autoPostEnabled ? (
                  <p className="mt-2 text-sm text-gray-600">
                    Hệ thống sẽ tự động viết và đăng bài theo thời gian đã cài
                    đặt
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-gray-600">
                    Hiện tại tính năng tự động viết và đăng bài tự động đã tắt.
                    Vui lòng bật để sử dụng
                  </p>
                )}
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-md">
                  <div className="pr-4">
                    <label className="block text-gray-700 font-medium">
                      Tự lặp lại khi đã đăng hết
                    </label>
                    <p className="mt-1 text-sm text-gray-600">
                      {autoPostEnabled
                        ? `Khi toàn bộ ${
                            activeProductInputType === "products"
                              ? "sản phẩm"
                              : "chủ đề"
                          } trong thiết lập đã đăng xong, hệ thống sẽ tự reset trạng thái và bắt đầu lại từ đầu.`
                        : `Bạn vẫn có thể bật sẵn tuỳ chọn này. Khi bật tự động viết và đăng bài, hệ thống sẽ lặp lại từ đầu sau khi đăng hết ${
                            activeProductInputType === "products"
                              ? "sản phẩm"
                              : "chủ đề"
                          }.`}
                    </p>
                  </div>
                  <div className="relative inline-block align-middle select-none shrink-0">
                    <input
                      type="checkbox"
                      name="repeatWhenExhausted"
                      id="repeatWhenExhausted"
                      checked={repeatWhenExhausted}
                      onChange={handleRepeatWhenExhaustedToggle}
                      className="hidden"
                    />
                    <label
                      htmlFor="repeatWhenExhausted"
                      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                      style={{
                        backgroundColor: repeatWhenExhausted
                          ? "#3b82f6"
                          : "#e5e7eb",
                      }}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                          repeatWhenExhausted
                            ? "translate-x-6"
                            : "translate-x-1"
                        }`}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Chế độ đăng bài - hiện cho tất cả khi autoPost bật, disable nếu chưa có gói Chuyên nghiệp */}
              {platform === "facebook" && autoPostEnabled && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="block text-gray-700 font-medium">
                      Chế độ đăng bài
                    </label>
                    {userMembership < 2 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                        🔒 Chuyên nghiệp
                      </span>
                    )}
                  </div>
                  <div
                    className={`flex rounded-lg overflow-hidden border border-gray-300 ${userMembership < 2 ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    <button
                      type="button"
                      onClick={() => handlePostModeChange("direct")}
                      className={`flex-1 py-2.5 px-4 text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                        postMode === "direct"
                          ? "bg-blue-600 text-white shadow-inner"
                          : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      ⚡ Đăng ngay
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePostModeChange("review")}
                      className={`flex-1 py-2.5 px-4 text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                        postMode === "review"
                          ? "bg-amber-500 text-white shadow-inner"
                          : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      👁 Duyệt trước
                    </button>
                  </div>
                  {userMembership < 2 ? (
                    <div className="mt-2 flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <span className="text-amber-600 text-sm">⬆️</span>
                      <p className="text-sm text-amber-700">
                        Nâng cấp lên gói <strong>Chuyên nghiệp</strong> để duyệt
                        bài viết trước khi đăng.
                      </p>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500">
                      {postMode === "direct"
                        ? "Hệ thống sẽ tự động viết và đăng bài ngay theo lịch đã cài đặt."
                        : "Bài viết sẽ được AI tạo và lưu vào hàng đợi duyệt. Bạn cần vào mục cấu hình để xem và duyệt trước khi đăng."}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-6">
                <div className="flex items-center justify-center gap-2 py-2 px-3 mb-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <span className="text-sm">💰</span>
                  <p className="text-sm text-gray-600">
                    Chi phí dự kiến:{" "}
                    <strong
                      className={
                        platform === "wordpress"
                          ? "text-blue-600"
                          : imageSource === IMAGE_SOURCES.AI
                            ? "text-purple-600"
                            : "text-green-600"
                      }
                    >
                      {getAutoPostCostLabel()}
                    </strong>
                  </p>
                </div>
                <button
                  onClick={saveAutoPostSettings}
                  className={`flex items-center justify-center w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-sm sm:text-base font-medium ${
                    isAutoPostSaveDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  disabled={isAutoPostSaveDisabled}
                >
                  {isSaving ? (
                    <>
                      <FiRefreshCw className="mr-2 h-5 w-5 animate-spin" />
                      Đang lưu...
                    </>
                  ) : (
                    <>
                      <FiSave className="mr-2 h-5 w-5" />
                      {platform !== "wordpress" &&
                      userPoint < requiredAutoPostPoints
                        ? "Không đủ điểm"
                        : platform === "wordpress" && !canUseWordpressAutoPost
                          ? "Không đủ điểm"
                          : "Lưu thiết lập đăng bài tự động"}
                    </>
                  )}
                </button>
                <div className="flex flex-col sm:flex-row gap-2 mt-2">
                  {config.id && (
                    <button
                      type="button"
                      onClick={deleteConfig}
                      className="flex items-center justify-center w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                      <FiTrash2 className="mr-2" />
                      Xóa thiết lập
                    </button>
                  )}
                  <button
                    onClick={resetConfig}
                    className="flex items-center justify-center w-full bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Tạo thiết lập mới
                  </button>
                </div>
              </div>

              {/* Review Queue Panel - hiện khi mode review + có config */}
              <ReviewQueuePanel
                userId={userId}
                configId={config.id}
                isVisible={
                  platform === "facebook" &&
                  postMode === "review" &&
                  !!config.id &&
                  userMembership >= 2
                }
              />
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[70] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Xác nhận xóa thiết lập
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Bạn có chắc chắn muốn xóa thiết lập này? Hành động này không thể
                hoàn tác.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-[70] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all duration-300 scale-100">
            <div className="p-6">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4 mx-auto text-amber-600 text-2xl">
                ⚠️
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                Yêu cầu nâng cấp gói dịch vụ!
              </h3>
              <p className="text-sm text-gray-600 mb-6 text-center leading-relaxed">
                Tính năng <strong>Tạo ảnh bằng AI tự động</strong> chỉ khả dụng từ gói <strong>Cao Cấp</strong> trở lên. Gói hiện tại của bạn chưa hỗ trợ tính năng này.
                <br /><br />
                Nâng cấp lên gói Cao Cấp để mở khóa tạo ảnh AI tự động không giới hạn cho bài viết, tích hợp đăng bài tự động lên Website WordPress và nhiều đặc quyền VIP khác!
              </p>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowUpgradeModal(false);
                    navigate("/pricing");
                  }}
                  className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200 text-center"
                >
                  🚀 Nâng cấp ngay
                </button>
                <button
                  type="button"
                  onClick={() => setShowUpgradeModal(false)}
                  className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors text-center"
                >
                  Để sau
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoPostGenerator;
